<template>
  <div>
    <ol class="breadcrumb float-xl-end">
      <li class="breadcrumb-item">
        <router-link to="/tasks">Munkafolyamatok</router-link>
      </li>
      <li class="breadcrumb-item active">{{ task.name }}</li>
    </ol>
    <handover-popover
      :task_id="$route.params.id"
      class="float-end"
    ></handover-popover>
    <risks-icon class="float-end"></risks-icon>
    <h1 class="page-header">
      Munkafolyamat szerkesztése
      <span
        class="badge bg-light text-red"
        v-if="task && !task.notfound && task.related_task_ids.length != 0"
      >
        <i class="fa fa-link"></i> Kapcsolódó feladatok:
        <router-link
          class="text-decoration-none fw-bold"
          :class="{
            'text-green': task_status.max_task_status == 70,
            'text-blue': task_status.max_task_status == 60,
            'text-red': task_status.max_task_status < 60,
          }"
          v-for="(task_status, id) in task.related_task_statuses"
          :key="id"
          :to="'/tasks/' + task_status.task_id + '/edit'"
        >
          #{{ task_status.task_id }}
        </router-link></span
      >
    </h1>
    <NoRecords
      v-if="task.notfound"
      main_icon="fas fa-user-lock"
      value="false"
      title="Nem található a munkafolyamat"
      subtitle="Menjen vissza a munkafolyamatokhoz!"
    ></NoRecords>
    <div class="row" v-if="!task.notfound">
      <div class="col-md-4">
        <details-panel
          :task="task"
          :security_types="security_types"
          :location_leads="location_leads"
          :ddc_users="ddc_users"
          :ddc_user="ddc_user"
          :partner_users="partner_users"
          :black_lock_user="black_lock_user"
          :workflow="workflow"
          :task_positions="task_positions"
          :task_statuses="task_statuses"
          :task_boxes="task_boxes"
          :task_comments="task_comments"
          :roles="roles"
          :userless_permit_count="userless_permit_count"
          :incomplete_narrow_permit_count="incomplete_narrow_permit_count"
          :isolations="isolations"
          :task_permit_types="task_permit_types"
          :is_task_positions_dirty="is_task_positions_dirty"
          @task_parameter_updated="taskParameterUpdated"
          @ddc_user_updated="updateDdcUser"
          @update_task_positions="updateTaskPositions"
          @black_lock_user_updated="updateBlackLockUser"
          @update_task_position_dirty="updateTaskPositionDirty"
        ></details-panel>
        <isolations-panel
          :isolations="isolations"
          :electricians="electricians"
          :electricians_high_voltage="electricians_high_voltage"
          :task="task"
        ></isolations-panel>
      </div>
      <div class="col-md-8">
        <panel title="Engedélyek">
          <div
            class="row"
            v-if="
              task.status <= 50 &&
              [3, 5].includes(user.role_id) &&
              !(
                task.crane &&
                task.status >= 50 &&
                task_permit_types.length == 0 &&
                isolations.length == 0
              )
            "
          >
            <label class="form-label">Engedélytípus hozzáadása:</label>
            <v-select
              v-if="permit_types"
              @option:selected="addPermitType"
              v-model="permit_type_to_add"
              :options="filtered_permit_types_array"
              label="name"
            >
              <template #search="{ attributes, events }">
                <input
                  class="vs__search"
                  v-bind="attributes"
                  v-on="events"
                  placeholder="Válassz"
                />
              </template>
            </v-select>
            <hr />
          </div>

          <Loading
            v-if="
              task_permit_types === false ||
              permit_types === false ||
              permit_local_roles === false ||
              users === false
            "
            title="engedélyek betöltése"
          ></Loading>
          <NoRecords
            v-else-if="task && normal_permits.length == 0"
            title="Nincsenek engedélyek"
            main_icon="fa fa-file-contract"
          ></NoRecords>
          <div
            class="row"
            v-else
            v-for="(task_permit_type, id) in normal_permits"
            :key="id"
          >
            <h3>
              <button
                class="btn btn-link text-red"
                title="engedély törlése"
                v-if="
                  [5, 7, 3].includes(user.role_id) &&
                  task_permit_type.permits.filter((p) => p.signed_at).length ==
                    0
                "
                @click="deletePermitType(task_permit_type.id)"
              >
                <i class="fa fa-trash"></i>
              </button>
              <span class="align-middle">{{
                permit_types[
                  permit_types
                    .map((p) => p.id)
                    .indexOf(task_permit_type.permit_type)
                ].name
              }}</span>
              <button
                v-if="
                  ((user.role_id == 5 && [task.owner_id].includes(user.id)) ||
                    (user.role_id == 3 &&
                      [task.location_leader_id].includes(user.id))) &&
                  task_permit_type.status <= 70 &&
                  [1, 2, 3, 4].includes(task_permit_type.permit_type)
                "
                v-b-modal="'add_permit_' + task_permit_type.id"
                class="btn btn-success float-end mx-1"
              >
                <i class="fa fa-user-plus"></i>
              </button>
              <button
                v-if="
                  (user.role_id == 5 &&
                    task.owner_id == user.id &&
                    task_permit_type.status == 10) ||
                  (task_permit_type.status <= 30 &&
                    (user.role_id == 3 || task.location_leader_id == user.id))
                "
                v-b-modal="'fill_detail_' + task_permit_type.id"
                class="btn btn-warning float-end mx-1"
                :disabled="
                  !isMatchingUser ||
                  task.status < 20 ||
                  hasNecessaryUsers(task_permit_type) == false
                "
              >
                <i class="fa fa-edit"></i>
              </button>
              <button
                v-if="
                  task_permit_type.status == 30 &&
                  task_permit_type.permits.length != 0 &&
                  (user.role_id != 3 ||
                    ![task.location_leader_id].includes(user.id))
                "
                class="btn btn-green float-end mx-1"
                disabled
              >
                Területvezetői jóváhagyásra vár
              </button>
              <button
                v-if="
                  task_permit_type.status == 30 &&
                  task_permit_type.permits.length != 0 &&
                  user.role_id == 3 &&
                  [task.location_leader_id].includes(user.id) &&
                  task_permit_type.permit_type == 1
                "
                class="btn btn-green float-end mx-1"
                :disabled="task_permit_type.accepted"
                v-b-modal="'fill_comment_' + task_permit_type.id"
              >
                Jóváhagyás
              </button>
              <button
                v-if="
                  task_permit_type.status == 30 &&
                  task_permit_type.permits.length != 0 &&
                  user.role_id == 3 &&
                  [task.location_leader_id].includes(user.id) &&
                  [2, 4, 3].includes(task_permit_type.permit_type) &&
                  (task_permit_type.permit_type != 2 ||
                    hasWatcherAndLeader(task_permit_type))
                "
                class="btn btn-green float-end mx-1"
                :disabled="
                  task_permit_type.accepted || task.status < 20 || loading
                "
                @click="confirmThenMove(task_permit_type)"
              >
                <i v-if="loading" class="fa fa-cog fa-spin"></i>
                Jóváhagyás
              </button>
              <button
                v-if="
                  [2].includes(task_permit_type.permit_type) &&
                  task_permit_type.permits.length > 0 &&
                  !hasWatcherAndLeader(task_permit_type)
                "
                class="btn btn-danger float-end mx-1"
                disabled
              >
                Irányító és figyelő is szükséges!
              </button>
              <button
                v-if="
                  [4].includes(task_permit_type.permit_type) &&
                  task_permit_type.permits.length > 0 &&
                  !hasWatcherAndAnyone(task_permit_type)
                "
                class="btn btn-danger float-end mx-1"
                disabled
              >
                Irányító és még egy személy szükséges!
              </button>
              <button
                v-if="
                  [30, 40].includes(task_permit_type.status) &&
                  task_permit_type.permits.length == 0
                "
                class="btn btn-danger float-end mx-1"
                disabled
              >
                Nincsenek hozzárendelt személyek!
              </button>
              <button
                v-if="
                  task_permit_type.status == 10 &&
                  user.role_id != 5 &&
                  ![task.owner_id].includes(user.id) &&
                  ![task.location_leader_id].includes(user.id)
                "
                class="btn btn-green float-end mx-1"
                disabled
              >
                Kitöltésre vár
              </button>
              <button
                v-if="task_permit_type.status == 20"
                class="btn btn-indigo float-end mx-1"
                disabled
              >
                Feszültségmentesítésre vár
              </button>
              <button
                v-if="
                  task_permit_type.status == 40 &&
                  task_permit_type.permits.length != 0 &&
                  (user.role_id != 5 || ![task.owner_id].includes(user.id))
                "
                class="btn btn-green float-end mx-1"
                disabled
              >
                Aláírásra vár
              </button>
              <button
                v-if="
                  task_permit_type.status == 50 &&
                  ((user.role_id == 5 && ![task.owner_id].includes(user.id)) ||
                    (user.role_id == 6 && user.id != task.partner_id) ||
                    (user.role_id == 3 &&
                      ![task.location_leader_id].includes(user.id)) ||
                    ![5, 6, 3].includes(user.role_id))
                "
                class="btn btn-green float-end mx-1"
                disabled
              >
                Aláírva, nyomtatható
              </button>
              <button
                v-if="
                  task_permit_type.status == 70 &&
                  !(user.role_id == 5 && task.owner_id == user.id) &&
                  !(user.role_id == 6 && task_permit_type.permit_type == 3)
                "
                class="btn btn-green float-end mx-1"
                disabled
              >
                <span v-if="[1, 4].includes(task_permit_type.permit_type)"
                  >PV </span
                >Visszaadásra vár
              </button>
              <button
                v-if="
                  task_permit_type.status == 80 &&
                  (user.role_id != 3 ||
                    ![task.location_leader_id].includes(user.id)) &&
                  task_permit_type.permit_type == 1
                "
                class="btn btn-primary float-end mx-1"
                disabled
              >
                Átadásra vár
              </button>
              <button
                v-if="
                  task_permit_type.status == 80 &&
                  (user.role_id != 3 ||
                    ![task.location_leader_id].includes(user.id)) &&
                  [2, 3].includes(task_permit_type.permit_type)
                "
                class="btn btn-primary float-end mx-1"
                disabled
              >
                Átvételre vár
              </button>
              <button
                v-if="
                  task_permit_type.status == 80 &&
                  (user.role_id == 3 ||
                    [task.location_leader_id].includes(user.id)) &&
                  task_permit_type.permit_type == 1
                "
                class="btn btn-primary float-end mx-1"
                @click="confirmThenMove(task_permit_type)"
              >
                Visszavétel
              </button>
              <button
                v-if="
                  task_permit_type.status == 80 &&
                  [2, 3].includes(task_permit_type.permit_type) &&
                  user.role_id == 3 &&
                  [task.location_leader_id].includes(user.id)
                "
                class="btn btn-primary float-end mx-1"
                v-b-modal="'fill_acceptance' + task_permit_type.id"
              >
                Átvétel
              </button>
              <button
                v-if="
                  task_permit_type.status == 80 &&
                  [4].includes(task_permit_type.permit_type) &&
                  user.role_id == 5 &&
                  [task.owner_id].includes(user.id)
                "
                class="btn btn-primary float-end mx-1"
                v-b-modal="'fill_acceptance' + task_permit_type.id"
              >
                Átvétel
              </button>
              <button
                v-if="
                  task_permit_type.status == 70 &&
                  user.role_id == 5 &&
                  [task.owner_id].includes(user.id) &&
                  task_permit_type.permit_type == 1
                "
                class="btn btn-success float-end mx-1"
                @click="bringBackHandler(task_permit_type)"
              >
                <i class="fa fa-arrow-rotate-left"></i> Visszahozás
              </button>
              <button
                v-if="
                  task_permit_type.status == 70 &&
                  ((user.role_id == 5 &&
                    task.owner_id == user.id &&
                    [2, 3, 4].includes(task_permit_type.permit_type)) ||
                    (user.id == task.partner_user_id &&
                      task_permit_type.permit_type == 3))
                "
                class="btn btn-success float-end mx-1"
                @click="bringBackHandler(task_permit_type)"
              >
                <i class="fa fa-arrow-rotate-left"></i>
                {{
                  user.id == task.partner_user_id ? "Visszaadás" : "Visszahozás"
                }}
              </button>
              <button
                v-if="
                  task_permit_type.status == 75 &&
                  task_permit_type.return_user_to_sign.id != user.id
                "
                class="btn btn-success float-end"
                disabled
              >
                <i class="fa fa-user"></i> Visszaadás alatt
              </button>
              <button
                v-if="
                  task_permit_type.status == 75 &&
                  task_permit_type.return_user_to_sign.id == user.id
                "
                class="btn btn-success float-end"
                @click="signReturn(task_permit_type.return_user_to_sign.log_id)"
              >
                <i class="fa fa-edit"></i> Visszaadás
              </button>
              <button
                v-if="
                  task_permit_type.status != 75 &&
                  ((((user.role_id == 5 && [task.owner_id].includes(user.id)) ||
                    (user.role_id == 3 &&
                      [task.location_leader_id].includes(user.id))) &&
                    task_permit_type.status >= 50) ||
                    (task_permit_type.status == 90 &&
                      task.status == 70 &&
                      [1, 3, 5].includes(user.role_id)))
                "
                class="btn btn-green float-end mx-1"
                @click="pdfButtonHandler(task_permit_type)"
              >
                <i class="fa fa-print"></i>
              </button>
              <button
                class="btn btn-red float-end mx-1"
                disabled
                v-if="task_permit_type.status == 75"
              >
                <i class="fa fa-print"></i>
              </button>
              <a
                v-if="task_permit_type.status > 20"
                class="btn btn-success float-end mx-1"
                target="_blank"
                :href="
                  back_url + '/document/view/permit/' + task_permit_type.id
                "
                ><i class="fa fa-eye"></i
              ></a>
            </h3>
            <h6>
              <span class="badge bg-dark" v-if="permit_statuses">{{
                permit_statuses.find((ps) => ps.id == task_permit_type.status)
                  .name
              }}</span>
              <template v-if="task_permit_type.end_date_time">
                <span>
                  Érvényesség:
                  {{ getSlicedDate(task_permit_type.start_date_time) }} -
                  {{ getSlicedDate(task_permit_type.end_date_time) }}
                </span>
                <button
                  v-b-modal="'approve_extension_' + task_permit_type.id"
                  v-if="task_permit_type.has_unresolved_validity"
                  class="btn btn-yellow btn-xs mx-1"
                  :disabled="
                    !(
                      user.role_id == 3 &&
                      [task.location_leader_id].includes(user.id)
                    )
                  "
                >
                  Aktív meghosszabbítási kérvény
                </button>
                <button
                  v-else-if="
                    ((user.role_id == 5 && [task.owner_id].includes(user.id)) ||
                      (user.role_id == 3 &&
                        [task.location_leader_id].includes(user.id))) &&
                        diffLessThanThreeDays(task_permit_type.end_date_time) &&
                    task_permit_type.status < 75
                  "
                  class="btn btn-indigo btn-xs mx-1"
                  v-b-modal="'fill_extension_' + task_permit_type.id"
                >
                  Meghosszabbítás
                </button>
              </template>
            </h6>
            <b-modal
              size="md"
              :id="'approve_extension_' + task_permit_type.id"
              hide-footer
              @shown="getValidityRecords(task_permit_type.id)"
            >
              <template slot="modal-header">
                <h4 class="modal-title">
                  {{
                    permit_types[
                      permit_types
                        .map((p) => p.id)
                        .indexOf(task_permit_type.permit_type)
                    ].name
                  }}
                  meghosszabbításai
                </h4>
                <a
                  class="btn-close"
                  @click="
                    $bvModal.hide('approve_extension_' + task_permit_type.id)
                  "
                ></a>
              </template>
              <b-container>
                <h5>
                  Érvényesség kezdete:
                  {{ getSlicedDate(task_permit_type.start_date_time) }}
                </h5>
                <Loading
                  v-if="!validity_records"
                  title="Érvényességi rekordok betöltése"
                ></Loading>
                <div v-else>
                  <h5>Érvényességi rekordok:</h5>
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Dátum és idő</th>
                        <th>Esemény</th>
                        <th>Státusz</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(v_rec, id) in validity_records" :key="id">
                        <td>{{ getSlicedDate(v_rec.end_date_time) }}</td>
                        <td>
                          {{
                            v_rec.end_date_time ==
                            task_permit_type.end_date_time
                              ? "érvényesség vége"
                              : "meghosszabbítva"
                          }}
                        </td>
                        <td>
                          <span v-if="v_rec.active == 1" class="badge bg-green"
                            ><i class="fa fa-check-circle"></i> jóváhagyva</span
                          >
                          <span
                            v-else-if="v_rec.deleted_at != null"
                            class="badge bg-danger"
                            ><i class="fa fa-times"></i> elutasítva</span
                          >
                          <div v-else>
                            <button
                              class="btn btn-xs btn-lime mx-1"
                              @click="validityActiveUpdate(v_rec.id, 1)"
                            >
                              Jóváhagyom
                            </button>
                            <button
                              class="btn btn-xs btn-danger mx-1"
                              @click="validityActiveUpdate(v_rec.id, 0)"
                            >
                              Elutasítom
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-container>
            </b-modal>
            <b-modal
              size="lg"
              :id="'fill_acceptance' + task_permit_type.id"
              cancel-title="Mégse"
              ok-title="Átvétel"
              @ok="
                movePermitStatus(task_permit_type.id, {
                  acceptance_val: acceptance_val,
                })
              "
              :ok-disabled="loading"
            >
              <template slot="modal-header">
                <h4 class="modal-title">Terület visszavétele</h4>
                <a
                  class="btn-close"
                  @click="
                    $bvModal.hide('fill_acceptance' + task_permit_type.id)
                  "
                ></a>
              </template>
              <b-container>
                <h5>
                  A munka befejeződött, a beszállásra adott engedély a
                  továbbiakban érvénytelen!
                </h5>
                <div class="form-group">
                  <div
                    class="form-check form-switch"
                    v-for="(rop, id) in acceptance_radio_options"
                    :key="id"
                  >
                    <label class="form-check-label">
                      <input
                        class="form-check-input"
                        type="radio"
                        :value="rop.id"
                        v-model="acceptance_val"
                      />
                      {{ rop.text }}
                    </label>
                  </div>
                </div>
              </b-container>
            </b-modal>
            <b-modal
              size="lg"
              :id="'fill_abandonment_' + task_permit_type.id"
              cancel-title="Mégse"
              ok-title="Munka felhagyása"
              @ok="
                movePermitStatus(task_permit_type.id, {
                  abandonment_values: abandonment_values,
                  signed_by: task_permit_type.return_user_to_sign.user_id,
                })
              "
              :ok-disabled="
                (!task_permit_type.signed_by_ddc_user &&
                  (!task_permit_type.user_sign ||
                    !task_permit_type.user_sign.data.signed_at)) ||
                loading
              "
              @shown="getUserSign(task_permit_type)"
            >
              <template slot="modal-header">
                <h4 class="modal-title">
                  {{
                    permit_types[
                      permit_types
                        .map((p) => p.id)
                        .indexOf(task_permit_type.permit_type)
                    ].name
                  }}
                  munka felhagyás
                </h4>
                <a
                  class="btn-close"
                  @click="
                    $bvModal.hide('fill_abandonment_' + task_permit_type.id)
                  "
                ></a>
              </template>
              <b-container>
                <h5 v-if="[1, 2, 3].includes(task_permit_type.permit_type)">
                  Kijelentem, hogy a fentiekben részletezett munkafolyamatot
                  elvégeztük, a munkaterületet biztonságos állapotban hagytuk
                  hátra. Irányításom alatt álló összes munkavállaló elhagyta a
                  területet.
                </h5>
                <div v-if="[2, 3].includes(task_permit_type.permit_type)">
                  <div class="form-group">
                    <label class="form-label">Energia visszakapcsolás</label>
                    <select
                      class="form-select"
                      v-model="abandonment_values.isolated"
                    >
                      <option :value="true">Megtörténhet</option>
                      <option :value="false">Nem történhet meg</option>
                    </select>
                  </div>
                  <div class="form-group mt-2">
                    <div
                      class="form-check form-switch"
                      v-for="(rop, id) in abandonment_radio_options"
                      :key="id"
                    >
                      <label class="form-check-label">
                        <input
                          class="form-check-input"
                          type="radio"
                          :value="rop.id"
                          v-model="abandonment_values.secure"
                        />
                        {{ rop.text }}
                      </label>
                    </div>
                  </div>
                </div>
                <div v-else-if="task_permit_type.permit_type == 4">
                  <h5>Kijelentem, hogy:</h5>
                  <div class="form-group mt-2">
                    <div
                      class="form-check form-switch"
                      v-for="(rop, id) in abandonment_radio_options_high"
                      :key="id"
                    >
                      <label class="form-check-label">
                        <input
                          class="form-check-input"
                          type="radio"
                          :value="rop.id"
                          v-model="abandonment_values.secure"
                        />
                        {{ rop.text }}
                      </label>
                    </div>
                  </div>
                </div>
                <div
                  class="form-group mt-3"
                  v-if="[2, 4, 3, 1].includes(task_permit_type.permit_type)"
                >
                  <label class="form-label">Aláíró személy:</label><br />
                  <v-select
                    v-model="task_permit_type.return_user_to_sign"
                    :options="task_permit_type.permits"
                    label="user_name"
                    :disabled="!!task_permit_type.user_sign"
                  >
                    <template #search="{ attributes, events }">
                      <input
                        class="vs__search"
                        v-bind="attributes"
                        v-on="events"
                        :placeholder="
                          !task_permit_type.return_user_to_sign ? 'Válassz' : ''
                        "
                      />
                    </template>
                  </v-select>
                  <!--
									<div class="mt-2" v-if="task_permit_type.return_user_to_sign">
											<PinSignatureSimple v-if="!task_permit_type.user_sign || !task_permit_type.user_sign.data.signed_at" :user_id="task_permit_type.return_user_to_sign.user_id" @signed="$set(task_permit_type,'signed_by_ddc_user',true)" :hideSignButton="!task_permit_type.return_user_to_sign.user_has_pin"></PinSignatureSimple>

											<button class="btn btn-yellow ms-2" v-if="!task_permit_type.user_sign" @click="sendForSign(task_permit_type)">Kiküldés bejelentkezéses aláírásra <i class="fa fa-user"></i></button>
											<span v-else-if="!task_permit_type.user_sign.data.signed_at" class="badge bg-green rounded-pill ms-2">Kiküldve bejelentkezéses aláírásra: <i class="fa fa-user"></i> {{users.find(u => u.id == task_permit_type.user_sign.data.user_to_sign).name}}</span>
											<span v-else class="badge bg-green rounded-pill ms-2">Aláírva rendszerbe bejelentkezve</span>
										</div>
									-->
                </div>
              </b-container>
              <template #modal-footer="{ cancel, ok }">
                <div class="row w-100">
                  <div class="col">
                    <b-button
                      class="float-start"
                      variant="danger"
                      @click="cancel()"
                    >
                      Mégse
                    </b-button>
                  </div>
                  <div class="col text-end">
                    <PinSignatureSimple
                      v-if="task_permit_type.return_user_to_sign"
                      :user_id="task_permit_type.return_user_to_sign.user_id"
                      @signed="ok()"
                      :hideSignButton="
                        !task_permit_type.return_user_to_sign.user_has_pin
                      "
                    >
                    </PinSignatureSimple>
                    <button v-else class="btn btn-green" disabled>
                      <i class="fa fa-signature"></i> Aláírás
                    </button>
                    <button
                      class="btn btn-yellow ms-2"
                      @click="
                        sendForSign(task_permit_type, {
                          abandonment_values: abandonment_values,
                          signed_by:
                            task_permit_type.return_user_to_sign.user_id,
                        })
                      "
                      :disabled="!task_permit_type.return_user_to_sign"
                    >
                      <i class="fa fa-user"></i> Kiküldés és felhagyás
                    </button>
                  </div>
                </div>
              </template>
            </b-modal>
            <b-modal
              size="md"
              :id="'fill_extension_' + task_permit_type.id"
              cancel-title="Mégse"
              ok-title="Meghosszabbítás"
              @ok="extendPermit(task_permit_type.id)"
              @shown="getValidityRecords(task_permit_type.id)"
            >
              <template slot="modal-header">
                <h4 class="modal-title">
                  {{
                    permit_types[
                      permit_types
                        .map((p) => p.id)
                        .indexOf(task_permit_type.permit_type)
                    ].name
                  }}
                  meghosszabbítása
                </h4>
                <a
                  class="btn-close"
                  @click="
                    $bvModal.hide('fill_extension_' + task_permit_type.id)
                  "
                ></a>
              </template>
              <b-container>
                <h5>
                  Érvényesség kezdete:
                  {{ getSlicedDate(task_permit_type.start_date_time) }}
                </h5>
                <Loading
                  v-if="!validity_records"
                  title="Érvényességi rekordok betöltése"
                ></Loading>
                <div v-else>
                  <h5>Érvényességi rekordok:</h5>
                  <table class="table">
                    <thead>
                      <tr>
                        <th>Dátum és idő</th>
                        <th>Esemény</th>
                        <th>Állapot</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(v_rec, id) in validity_records" :key="id">
                        <td>{{ getSlicedDate(v_rec.end_date_time) }}</td>
                        <td>
                          {{
                            v_rec.end_date_time ==
                            task_permit_type.end_date_time
                              ? "érvényesség vége"
                              : "meghosszabbítás"
                          }}
                        </td>
                        <td>
                          <p v-if="v_rec.active">Elfogadva</p>
                          <p v-else-if="v_rec.deleted_at == null">
                            Jóváhagyásra vár
                          </p>
                          <p v-else>Elutasítva</p>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <label class="form-label"
                  >Ez az engedély a következő dátumig érvényes:</label
                >
                <div class="row">
                  <div class="col-md-8">
                    <div class="form-group mt-2">
                      <input
                        type="date"
                        class="form-control"
                        v-model="end_date"
                        :min="
                          task_permit_type.end_date_time | moment('yyyy-MM-DD')
                        "
                      />
                    </div>
                  </div>
                  <div class="col-md-4">
                    <div class="form-group mt-2">
                      <input
                        v-if="with_time"
                        type="time"
                        class="form-control"
                        v-model="end_time"
                      />
                    </div>
                  </div>
                </div>
                <div class="form-check mt-2">
                  <input
                    class="form-check-input"
                    :true-value="false"
                    :false-value="true"
                    type="checkbox"
                    v-model="with_time"
                    id="with_time_input"
                  />
                  <label class="form-check-label" for="with_time_input"
                    >Egész nap</label
                  >
                </div>
              </b-container>
            </b-modal>
            <b-modal
              size="l"
              :id="'fill_comment_' + task_permit_type.id"
              cancel-title="Mégse"
              ok-title="Jóváhagyás"
              @ok="movePermitStatus(task_permit_type.id, { comment: comment })"
              :ok-disabled="loading"
            >
              <template slot="modal-header">
                <h4 class="modal-title">
                  {{
                    permit_types[
                      permit_types
                        .map((p) => p.id)
                        .indexOf(task_permit_type.permit_type)
                    ].name
                  }}
                  Jóváhagyás
                </h4>
                <a
                  class="btn-close"
                  @click="$bvModal.hide('fill_comment_' + task_permit_type.id)"
                ></a>
              </template>
              <b-container>
                <label class="form-label"
                  >Üzemvezetői/területvezetői megjegyzés:</label
                >
                <textarea
                  cols="30"
                  rows="10"
                  class="form-control"
                  v-model="comment"
                ></textarea>
              </b-container>
            </b-modal>
            <b-modal
              size="xl"
              :id="'fill_detail_' + task_permit_type.id"
              cancel-title="Mégse"
              ok-title="Mentés"
              @ok="sendFormData($event, task_permit_type.id)"
              :ok-disabled="loading"
            >
              <template slot="modal-header">
                <h4 class="modal-title">
                  {{
                    permit_types[
                      permit_types
                        .map((p) => p.id)
                        .indexOf(task_permit_type.permit_type)
                    ].name
                  }}
                  kitöltése
                </h4>
                <a
                  class="btn-close"
                  @click="$bvModal.hide('fill_detail_' + task_permit_type.id)"
                ></a>
              </template>
              <b-container>
                <Loading v-if="loading" title="Mentés"></Loading>
                <component
                  v-show="!loading"
                  v-bind:is="
                    'PermitForm' +
                    ([2, 3].includes(task_permit_type.permit_type)
                      ? 2
                      : task_permit_type.permit_type)
                  "
                  :ref="'permit_form_' + task_permit_type.id"
                  :permit_type="task_permit_type"
                  :task="task"
                  @closemodal="closeModal"
                  @iscomplete="updateIsComplete($event, task_permit_type.id)"
                  @formdata="updateFormData($event, task_permit_type.id)"
                  @loadingstop="loading = false"
                >
                </component>
              </b-container>
            </b-modal>
            <b-modal
              :id="'add_permit_' + task_permit_type.id"
              cancel-title="Mégse"
              ok-title="Hozzáadás"
              @ok="addPermitToTask"
              @shown="permitToAddInitializer(task_permit_type)"
            >
              <template slot="modal-header">
                <h4 class="modal-title">
                  Új felhasználó hozzáadása
                  {{
                    permit_types[
                      permit_types
                        .map((p) => p.id)
                        .indexOf(task_permit_type.permit_type)
                    ].name
                  }}hez
                </h4>
                <a
                  class="btn-close"
                  @click="$bvModal.hide('add_permit_' + task_permit_type.id)"
                ></a>
              </template>
              <b-container>
                <Loading v-if="loading" title="Létrehozás"></Loading>
                <form v-else @submit.prevent="addPermitToTask()">
                  <div
                    class="alert alert-danger show"
                    v-if="
                      [2].includes(task_permit_type.permit_type) &&
                      task_permit_type.permits.length > 0 &&
                      !hasWatcherAndLeader(task_permit_type)
                    "
                  >
                    <i class="fa fa-info-circle me-1"></i>
                    <strong
                      >Figyelem, ehhez az engedélyhez irányító és figyelő is
                      szükséges!</strong
                    >
                  </div>
                  <div class="form-group mb-2">
                    <label class="fomr-label mb-1">Felhasználó:</label>
                    <v-select
                      v-if="users"
                      v-model="permit_to_add.user_id"
                      :options="users_for_permits(task_permit_type)"
                      :reduce="(option) => option.id"
                      label="name"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          :class="{ 'is-invalid': errors.user_id }"
                          v-bind="attributes"
                          v-on="events"
                          :placeholder="!permit_to_add.user_id ? 'Válassz' : ''"
                          :required="!permit_to_add.user_id"
                        />
                      </template>
                    </v-select>
                    <div v-if="errors.user_id" class="invalid-feedback d-block">
                      {{ errors.user_id[0] }}
                    </div>
                  </div>
                  <div class="form-group">
                    <label class="fomr-label mb-1">Szerep:</label>
                    <v-select
                      v-if="permit_local_roles"
                      v-model="permit_to_add.local_role"
                      :options="roles_available_for_permit"
                      :reduce="(option) => option.id"
                      label="name"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          :class="{ 'is-invalid': errors.local_role }"
                          v-bind="attributes"
                          v-on="events"
                          :placeholder="
                            !permit_to_add.local_role ? 'Válassz' : ''
                          "
                          :required="!permit_to_add.local_role"
                        />
                      </template>
                    </v-select>
                    <div
                      v-if="errors.local_role"
                      class="invalid-feedback d-block"
                    >
                      {{ errors.local_role[0] }}
                    </div>
                  </div>
                </form>
              </b-container>
            </b-modal>
            <div class="table-responsive" v-if="task_permit_type.status != 90">
              <table class="table table-hover">
                <tbody>
                  <tr
                    v-for="(task_permit, id) in task_permit_type.permits"
                    :key="id"
                  >
                    <td
                      class="align-middle"
                      v-if="
                        ([task.owner_id].includes(user.id) &&
                          user.role_id == 5) ||
                        (user.role_id == 3 &&
                          [task.location_leader_id].includes(user.id))
                      "
                      style="width: 100px"
                    >
                      <button
                        v-if="
                          ([task.owner_id].includes(user.id) &&
                            user.role_id == 5 &&
                            !task_permit.signed_at) ||
                          (user.role_id == 3 &&
                            [task.location_leader_id].includes(user.id) &&
                            !task_permit.signed_at)
                        "
                        @click="deletePermit(task_permit)"
                        class="btn btn-link"
                      >
                        <i class="fa fa-delete-left fa-2x text-danger"></i>
                      </button>
                    </td>
                    <td class="align-middle">{{ task_permit.user_name }}</td>
                    <td class="align-middle">
                      {{
                        permit_local_roles.find(
                          (p) => p.id == task_permit.local_role
                        ).name
                      }}
                    </td>
                    <td
                      class="align-middle text-end"
                      v-if="
                        ([task.owner_id].includes(user.id) &&
                          user.role_id == 5 &&
                          task_permit_type.status > 30) ||
                        (user.role_id == 3 &&
                          [task.location_leader_id].includes(user.id) &&
                          task_permit_type.status > 30)
                      ">
                      <PinSignature
                        :key="
                          task_permit.id +
                          '_' +
                          task_permit.user_id +
                          '_' +
                          task_permit.local_role
                        "
                        :permit_id="task_permit.id"
                        :hideEmailButton="
                          !task_permit.notifiable_by_email ||
                          ![5, 3].includes(user.role_id)
                        "
                        :hideSignButton="!task_permit.user_has_pin"
                        :notified="task_permit.notified_at"
                        :signed="task_permit.signed_at"
                        @signed="setSigned(task_permit, $event)"
                      ></PinSignature>
                    </td>
                    
                    <td
                      class="align-middle text-end"
                      v-else-if="
                        task_permit.user_id == user.id &&
                        task_permit_type.status >= 40 && task_permit_type.status <= 80
                      ">
                      <button
                        v-if="!task_permit.signed_at"
                        class="btn btn-yellow"
                        @click="signLoggedIn(task_permit)"
                      >
                        <i class="fa fa-signature"></i> Aláírom
                      </button>
                      <span v-else class="badge bg-green"
                        ><i class="fa fa-check-circle"></i> aláírva:
                        {{ task_permit.signed_at }}</span
                      >
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <h3 v-else>
              <i class="fa fa-check-circle text-green"></i> Engedély visszahozva
            </h3>
            <hr />
          </div>
        </panel>
        <panel
          title="Munkaterület átadási/átvételi engedély"
          :noButton="true"
          v-if="
            task_permit_types &&
            task_permit_types.filter((t) => t.permit_type == 5).length > 0 &&
            this.partner &&
            this.partner_users !== false
          "
        >
          <template #headerTools>
            <button
              class="btn btn-xs rounded-pill btn-blue px-4"
              v-b-modal.additionalAreaPermitModal
              v-if="
                task.owner_id == user.id &&
                user.role_id == 5 &&
                task_permit_types.filter((t) => t.permit_type == 5).length >=
                  1 &&
                !activeAreaPermit
              "
            >
              <i class="fa fa-plus"></i> Engedély hozzáadása
            </button>
          </template>
          <b-modal
            id="additionalAreaPermitModal"
            @shown="initializeAreaPermitForm"
            cancel-variant="default"
            @cancel="closeAreaPermitModal"
            @ok="storeAreaPermit"
            ok-title="Hozzáadás"
            cancel-title="Mégse"
            :ok-disabled="loading || hasBlackLocksReturned"
            size="lg"
          >
            <template slot="modal-header">
              <h4 class="modal-title">
                Munkaterület átadási/átvételi engedély hozzáadása
              </h4>
              <a class="btn-close" @click="closeAreaPermitModal"></a>
            </template>
            <div class="alert alert-danger" v-if="hasBlackLocksReturned == null">loading...</div>
            <div class="alert alert-danger" v-else-if="hasBlackLocksReturned">
              Legalább egy fekete lakatot már visszaadtak, ezért nem adható ki újabb engedély.
            </div>
            <form @submit.prevent="storeAreaPermit" v-else-if="users">
              <div class="mb-2">
                <label class="form-label">Vállalkozó cég vezetője:</label>
                <v-select
                  v-model="areaPermitForm.user_id"
                  :options="partner_users"
                  label="name"
                  :reduce="(option) => option.id"
                >
                  <template #search="{ attributes, events }">
                    <input
                      class="vs__search"
                      v-bind="attributes"
                      v-on="events"
                      :placeholder="
                        areaPermitForm.user_id == null ? 'Válassz' : ''
                      "
                    />
                  </template>
                </v-select>
              </div>
              <div class="mb-2">
                <h6>
                  <a class="help-anchor btn btn-rounded btn-warning">
                    ?
                    <div class="help-text text-black">
                      <p>
                        <strong>teljeskörű munkaterület átadás:</strong> A
                        Munkaterület átadás-átvételi engedélyben megjelölt
                        munkaterület átadása a munkát végző vállalkozó
                        (továbbiakban megbízott vállalkozó) részére. A
                        munkaterületet átadó (területgazda és projektvezető)
                        garantálja, hogy a megbízott vállalkozó részére
                        teljeskörűen átadott munkaterületen a megbízó
                        munkavállalói, illetve más vállalkozó - ez alól kivétel,
                        a megbízott vállalkozó által bevont vállalkozó - az
                        átadás időtartamáig munkát nem fog végezni. A teljes
                        munkaterület átadás során előfordulhat, hogy üzemelő
                        berendezések környezetében kell a megbízottnak munkát
                        végezni. Ebben az esetben, ennek veszélyeit, kockázatait
                        és a betartandó biztonsági szabályokat a megbízó
                        (területvezető és/vagy projektvezető) ismerteti a
                        megbízott vállalkozóval.
                      </p>
                      <p>
                        <strong>részleges terület átadás:</strong> A
                        Munkaterület átadás-átvételi engedélyben megjelölt
                        munkaterület átadása a munkát végző vállalkozó
                        (továbbiakban megbízott vállalkozó) részére csak
                        részleges, azt jelenti, hogy az adott területen a
                        munkaterületet átadó (területgazda és projektvezető)
                        munkavállalói, illetve más vállalkozó munkavállalói is
                        végezhetnek munkát az átadás időtartama alatt. Ebben az
                        esetben a munkafolyamatok összehangolása a
                        munkaterületet átadó felelőssége, amennyiben azt
                        dokumentáltan nem adja át valamelyik vállalkozónak.
                      </p>
                      <p>
                        Munkavégzés helyének minősül a munkaszervezéssel
                        összefüggő felvonulási, előkészítési, valamint a munka
                        elvégzéséhez szükséges építési anyagok, gépek,
                        szerkezetek, szerelvények és felvonulási épületek
                        elhelyezésére, valamint az előkészítő munkafolyamatok
                        elvégzésére szolgáló terület.
                      </p>
                    </div>
                  </a>
                  Összehangolás:
                </h6>
                <div class="row">
                  <div class="col-6 text-center">
                    <span class="d-block mb-2">kizárólagos használatra</span>
                    <input
                      type="radio"
                      class="form-check-input"
                      v-model="areaPermitForm.coordination"
                      :value="1"
                      id="coordination_radio_input_1"
                    />
                  </div>
                  <div class="col-6 text-center">
                    <span class="d-block mb-2"
                      >részleges használatra, ahol az összehangolást végzi</span
                    >
                    <div class="row my-2">
                      <div class="col-6">
                        <input
                          type="radio"
                          class="form-check-input"
                          v-model="areaPermitForm.coordination"
                          :value="2"
                          id="coordination_radio_input_2"
                        />
                        <label for="coordination_radio_input_2" class="form-check-label">
                          DDC</label
                        >
                      </div>
                      <div class="col-6">
                        <input
                          type="radio"
                          class="form-check-input"
                          v-model="areaPermitForm.coordination"
                          :value="3"
                          id="coordination_radio_input_3"
                        />
                        <label for="coordination_radio_input_3" class="form-check-label">
                          külső vállalkozó cég</label
                        >
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-12">
                        <div class="form-group mb-3" v-if="areaPermitForm.coordination == 2">
                          <label class="form-label"
                            >Összehangolást végző munkavállaló:</label
                          >
                          <v-select
                            v-if="users"
                            v-model="areaPermitForm.coordination_user_id"
                            :options="users.filter((u) => [3, 5].includes(u.role_id))"
                            :reduce="(option) => option.id"
                            label="name"
                          >
                            <template #search="{ attributes, events }">
                              <input
                                class="vs__search"
                                :required="!areaPermitForm.coordination_user_id"
                                v-bind="attributes"
                                v-on="events"
                                :placeholder="!areaPermitForm.coordination_user_id ? 'Válassz' : ''"
                              />
                            </template>
                          </v-select>
                        </div>
                        <div class="form-group mb-3" v-if="areaPermitForm.coordination == 3">
                          <label class="form-label"
                            >Összehangolást végző vállalkozó cég:</label
                          >
                          <v-select
                            v-if="partners"
                            v-model="areaPermitForm.coordination_partner_id"
                            :options="partners"
                            :reduce="(option) => option.id"
                            label="name"
                          >
                            <template #search="{ attributes, events }">
                              <input
                                class="vs__search"
                                :required="!areaPermitForm.coordination_partner_id"
                                v-bind="attributes"
                                v-on="events"
                                :placeholder="!areaPermitForm.coordination_partner_id ? 'Válassz' : ''"
                              />
                            </template>
                          </v-select>
                        </div>
                        <div
                          class="form-group mb-3"
                          v-if="areaPermitForm.coordination && areaPermitForm.coordination_partner_id"
                        >
                          <label class="form-label"
                            >Összehangolást végző vállalkozó cég vezetője:</label
                          >
                          <v-select
                            v-if="third_party_partner_workers"
                            v-model="areaPermitForm.coordination_user_id"
                            :options="third_party_partner_workers"
                            :reduce="(option) => option.id"
                            label="name"
                          >
                            <template #search="{ attributes, events }">
                              <input
                                class="vs__search"
                                :required="!areaPermitForm.coordination_user_id"
                                v-bind="attributes"
                                v-on="events"
                                :placeholder="!areaPermitForm.coordination_user_id ? 'Válassz' : ''"
                              />
                            </template>
                          </v-select>
                          <Loading
                            title="vállalkozó cég munkavállalóinak betöltése"
                            v-else
                          ></Loading>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                
              </div>
              
              
            </form>
            <loading v-else></loading>
          </b-modal>
          <AreaPermit
            v-for="(area_permit, id) in areaPermits"
            :key="id"
            :task="task"
            :area_permit="area_permit"
            :partner="partner"
            :users="users"
            :ddc_employees="ddc_employees" 
            :partners="partners"
          >
          </AreaPermit>
        </panel>
        <b-overlay
          :show="
            ![3, 5, 7].includes(user.role_id) ||
            (user.role_id == 3 && user.id != task.location_leader_id) ||
            (user.role_id == 5 && user.id != task.owner_id)
          "
          rounded="sm"
          opacity="0.4"
        >
          <CranePanel
            :task_id="task.id"
            :location_id="task.location_id"
            v-if="task"
            :crane_needed="task.crane"
            :can_add="
              task.status < 70 &&
              !(
                ![3, 5, 7].includes(user.role_id) ||
                (user.role_id == 3 && user.id != task.location_leader_id) ||
                (user.role_id == 5 && user.id != task.owner_id)
              )
            "
          ></CranePanel>
          <template #overlay>
            <div class="text-center">
              <h3><i class="fa fa-exclamation-triangle"></i></h3>
              <strong
                >Nem te vagy a hozzárendelt területvezető, vagy
                projektvezető</strong
              >
            </div>
          </template>
        </b-overlay>
        <panel title="Lakatok">
          <Loading
            v-if="task === false || users === false"
            title="Hajtások betöltése"
          ></Loading>
          <NoRecords
            v-else-if="task && task_positions.length == 0"
            title="Nincsenek Hajtások"
            main_icon="fa fa-bolt"
          ></NoRecords>
          <div v-else>
            <div
              v-if="
                user.role_id == 3 && task.status == 15 && task_boxes.length == 0
              "
            >
              <label class="form-label">Hajtás hozzáadása:</label>
              <v-select
                v-if="positions"
                @option:selected="addPosition"
                v-model="position_to_add"
                :options="
                  positions.map((m) => ({
                    id: m.id,
                    full_name: m.full_name,
                  }))
                "
                label="full_name"
              >
                <template #search="{ attributes, events }">
                  <input
                    class="vs__search"
                    v-bind="attributes"
                    v-on="events"
                    placeholder="Válassz"
                  />
                </template>
              </v-select>
            </div>
            <div v-if="user.role_id == 3 && task.status < 30">
              <b-overlay :show="task.status == 10" rounded="sm" opacity="0.4">
                <table class="table table-hover table-bordered">
                  <thead>
                    <tr>
                      <th>Megnevezés</th>
                      <th>Feszültségmentesítés</th>
                      <th>Lakatolás</th>
                    </tr>
                  </thead>
                  <tbody v-if="user.role_id == 3">
                    <tr v-for="(position, id) in this.task_positions" :key="id">
                      <td class="align-middle">
                        {{ position.position_simple.name }} -
                        {{ position.position_simple.position_id }}
                      </td>
                      <td class="align-middle">
                        <Loading mini v-if="!positions"></Loading>
                        <div v-else class="text-center">
                          <label
                            class="my-auto"
                            :for="'unplug_checkbox_input' + id"
                            :class="{
                              'text-orange':
                                position.unplug == 1 &&
                                !positions[
                                  positions
                                    .map((p) => p.id)
                                    .indexOf(position.position_id)
                                ].is_high_voltage,
                              'text-danger':
                                position.unplug == 1 &&
                                positions[
                                  positions
                                    .map((p) => p.id)
                                    .indexOf(position.position_id)
                                ].is_high_voltage,
                              'text-muted': position.unplug == 0,
                            }"
                            ><i class="fa fa-bolt fa-2x"></i
                          ></label>
                          <input
                            class="d-none"
                            type="checkbox"
                            v-model="position.unplug"
                            @click="checkPosition(position, 1)"
                            true-value="1"
                            false-value="0"
                            :id="'unplug_checkbox_input' + id"
                            :disabled="
                              positions.find(
                                (p) => p.id == position.position_id
                              ).is_high_voltage == 1 ||
                              positions.find(
                                (p) => p.id == position.position_id
                              ).non_isolated == 1 ||
                              task.status >= 30
                            "
                          />
                        </div>
                      </td>
                      <td class="align-middle">
                        <Loading mini v-if="!positions"></Loading>
                        <div class="text-center" v-else>
                          <label
                            :for="'locking_checkbox_input' + id"
                            :class="{
                              'text-orange':
                                position.locking == 1 &&
                                !positions[
                                  positions
                                    .map((p) => p.id)
                                    .indexOf(position.position_id)
                                ].is_high_voltage,
                              'text-danger':
                                position.locking == 1 &&
                                positions[
                                  positions
                                    .map((p) => p.id)
                                    .indexOf(position.position_id)
                                ].is_high_voltage,
                              'text-muted': position.locking == 0,
                            }"
                            ><i class="fa fa-lock fa-2x"></i
                          ></label>
                          <input
                            class="d-none"
                            type="checkbox"
                            v-model="position.locking"
                            @click="checkPosition(position, 2)"
                            true-value="1"
                            false-value="0"
                            :id="'locking_checkbox_input' + id"
                            :disabled="
                              positions[
                                positions
                                  .map((p) => p.id)
                                  .indexOf(position.position_id)
                              ].is_high_voltage == 1 || task.status >= 30
                            "
                          />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div class="w-100 text-end" v-if="task.status < 30">
                  <button
                    class="btn btn-green ml-auto mr-0"
                    @click="savePositionsLockingUnplug"
                    v-if="is_task_positions_dirty"
                  >
                    <i class="fa fa-save"></i> Mentés
                  </button>
                </div>
                <template #overlay>
                  <div class="text-center">
                    <h3><i class="fa fa-stopwatch"></i></h3>
                    <strong>Várakozás projektvezetői átadásra</strong>
                  </div>
                </template>
              </b-overlay>
            </div>
            <div v-else>
              <b-overlay
                :show="user.role_id == 7 && task.operator_id != user.id"
                rounded="sm"
                opacity="0.4"
              >
                <div v-if="task && task_boxes && task_boxes.length != 0">
                  <div class="row">
                    <div
                      class="col-lg-12"
                      v-for="(box, id) in task_boxes"
                      :key="id"
                    >
                      <div class="widget widget-stats bg-yellow">
                        <div
                          class="widget-content text-black-strict bg-opacity-90 bg-white p-3"
                        >
                          <div class="row">
                            <div class="col-lg-8">
                              <h1 class="text-center p-1">
                                <span
                                  class="float-start bg-yellow text-dark rounded-circle fs-3 p-1"
                                  >{{ box.number }}</span
                                >
                                {{ box.name }}
                              </h1>
                              <div
                                v-for="(position, id) in task_positions.filter(
                                  (p) => p.box_id == box.id
                                )"
                                :key="id"
                              >
                                <h3>
                                  <i
                                    class="fa fa-lock"
                                    :class="{
                                      'text-yellow': position.locking == 1,
                                      'text-muted': position.locking == 0,
                                    }"
                                  ></i>
                                  {{ position.position_simple.name }} -
                                  {{ position.position_simple.position_id }}
                                </h3>
                              </div>
                            </div>
                            <div class="col-lg-4">
                              <div
                                class="row text-center h-50 d-flex align-items-center"
                              >
                                <SelectLock
                                  :user="ddc_user"
                                  :color="1"
                                  v-if="
                                    box.key_at_task &&
                                    box.blue_lock_id == null &&
                                    box.key_at_task != task.id &&
                                    box.key_at_task !== true &&
                                    task.status < 50 &&
                                    !task.partner_id
                                  "
                                  @lock_selected="
                                    boxLockSelected(box, $event, 1)
                                  "
                                  :button_disabled="user.role_id != 7"
                                  :locks="blue_locks"
                                  :position_id="box.id"
                                  btnClass="btn-lg"
                                  lockIconClass="fa fa-3x"
                                ></SelectLock>
                                <span
                                  class="badge bg-blue"
                                  v-else-if="
                                    box.key_at_task &&
                                    box.blue_lock_id == null &&
                                    box.key_at_task != task.id &&
                                    box.key_at_task !== true &&
                                    task.status >= 50 &&
                                    !task.partner_id
                                  "
                                  ><i class="fa fa-check"></i> lakat
                                  visszahozva</span
                                >
                                <div
                                  class="input-group"
                                  v-else-if="box.blue_lock_id == null"
                                >
                                  <span
                                    class="input-group-text"
                                    v-if="
                                      user.role_id == 7 &&
                                      user.id == task.operator_id &&
                                      (task.status < 50 || task.status == 60)
                                    "
                                  >
                                    <input
                                      @change="boxCheckHandle(box)"
                                      :disabled="user.role_id != 7"
                                      class="form-check-input"
                                      type="checkbox"
                                      v-model="box.key_at_task"
                                      v-if="task.status < 50"
                                    />
                                    <button
                                      class="bg-white"
                                      @click="takeBackKey(box)"
                                      v-else-if="box.key_at_task"
                                    >
                                      <i
                                        class="fa fa-arrow-rotate-right fa-2x text-warning"
                                      ></i>
                                    </button>
                                    <i class="fa fa-check text-blue" v-else></i>
                                  </span>
                                  <span
                                    class="form-control"
                                    v-if="
                                      task.status < 50 &&
                                      user.role_id == 7 &&
                                      user.id == task.operator_id
                                    "
                                    >kiadtam a lakatszekrény kulcsát</span
                                  >
                                  <span
                                    class="form-control"
                                    v-else-if="task.status < 50"
                                    >{{
                                      box.key_at_task
                                        ? "Lakatszekrény kulcs kiadva"
                                        : "Lakatszekrény kulcs kiadásra vár"
                                    }}</span
                                  >
                                  <span
                                    class="form-control"
                                    v-else-if="
                                      box.key_at_task &&
                                      user.role_id == 7 &&
                                      user.id == task.operator_id
                                    "
                                    >kulcs visszahozás</span
                                  >
                                  <span
                                    class="form-control"
                                    v-else-if="box.key_at_task"
                                    >kulcs visszahozásra vár</span
                                  >
                                  <span class="form-control" v-else
                                    >kulcs visszahozva</span
                                  >
                                </div>
                                <div class="input-group" v-else>
                                  <button
                                    class="input-group-text bg-white"
                                    @click="deselectLock(box, 1)"
                                    v-if="
                                      user.role_id == 7 &&
                                      user.id == task.operator_id &&
                                      task.status < 50
                                    "
                                  >
                                    <i
                                      class="fa fa-delete-left fa-2x text-red"
                                    ></i>
                                  </button>
                                  <button
                                    class="input-group-text bg-white"
                                    @click="returnBoxLock(box, 1)"
                                    v-else-if="
                                      user.role_id == 7 &&
                                      user.id == task.operator_id &&
                                      (task.status < 50 || task.status == 60)
                                    "
                                  >
                                    <i
                                      class="fa fa-arrow-rotate-right fa-2x text-warning"
                                    ></i>
                                  </button>
                                  <span class="form-control"
                                    ><i class="fa fa-lock fa-2x text-blue"></i>
                                    # {{ box.blue_lock_name }}</span
                                  >
                                </div>
                              </div>
                              <div
                                class="row text-center h-50 d-flex align-items-center mt-2"
                                v-if="task.partner_id"
                              >
                                <SelectLock
                                  :user="black_lock_user"
                                  v-if="
                                    box.black_lock_id == null &&
                                    task.status < 50
                                  "
                                  :color="4"
                                  @lock_selected="
                                    boxLockSelected(box, $event, 4)
                                  "
                                  :button_disabled="user.role_id != 7"
                                  :locks="black_locks"
                                  :position_id="box.id"
                                  btnClass="bg-black-strict btn-lg"
                                  lockIconClass="fa fa-3x"
                                  :ref="'black_lock_select_' + box.id"
                                ></SelectLock>
                                <span
                                  class="badge bg-black-strict"
                                  v-else-if="
                                    box.black_lock_id == null &&
                                    task.status >= 50
                                  "
                                  ><i class="fa fa-check"></i> lakat
                                  visszahozva</span
                                >
                                <div class="input-group" v-else>
                                  <button
                                    class="input-group-text bg-white"
                                    @click="deselectLock(box, 4)"
                                    v-if="
                                      user.role_id == 7 &&
                                      user.id == task.operator_id &&
                                      task.status < 50
                                    "
                                  >
                                    <i
                                      class="fa fa-delete-left fa-2x text-red"
                                    ></i>
                                  </button>
                                  <button
                                    class="input-group-text bg-white"
                                    @click="returnBoxLock(box, 4)"
                                    v-else-if="blackLockReadyForReturn()"
                                  >
                                    <i
                                      class="fa fa-arrow-rotate-right fa-2x text-warning"
                                    ></i>
                                  </button>
                                  <span class="form-control"
                                    ><i
                                      class="fa fa-lock fa-2x text-black-strict"
                                    ></i>
                                    # {{ box.black_lock_name }}</span
                                  >
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <b-modal
                      id="sign-key"
                      @cancel="cancelBoxKeyModal()"
                      no-close-on-backdrop
                      no-close-on-esc
                      @ok="boxSendHandle()"
                    >
                      <template #modal-header="{ cancel }">
                        <h4 class="modal-title">
                          <Loading
                            mini
                            class="d-inline-block"
                            v-if="!box_to_modal.box"
                          ></Loading>
                          <span v-else># {{ box_to_modal.box.number }} </span>
                          szekrény kulcs
                          <Loading
                            mini
                            class="d-inline-block"
                            v-if="!box_to_modal.box"
                          ></Loading>
                          <span v-else>{{
                            box_to_modal.box.key_at_task === true
                              ? "kiadás"
                              : "visszavétel"
                          }}</span>
                        </h4>
                        <a class="btn-close" @click="cancel()"></a>
                      </template>
                      <Loading
                        v-if="!box_to_modal.box || !box_to_modal.user"
                      ></Loading>
                      <div class="text-center" v-else>
                        <h2 class="fw-light">Alulírott</h2>
                        <h2
                          class="fw-bold"
                          v-if="box_to_modal.box.key_at_task === true"
                        >
                          {{ box_to_modal.user.name }}
                        </h2>
                        <v-select
                          v-else
                          v-model="box_to_modal.user"
                          :options="ddc_users.filter((u) => u.pin_set)"
                          label="name"
                          class="w-80"
                          :clearable="false"
                        >
                          <template #search="{ attributes, events }">
                            <input
                              class="vs__search"
                              v-bind="attributes"
                              v-on="events"
                              :placeholder="
                                box_to_modal.user == null ? 'Válassz' : ''
                              "
                            />
                          </template>
                        </v-select>
                        <h3 class="fw-light">
                          {{
                            box_to_modal.box.key_at_task === true
                              ? "átveszem"
                              : "visszahozom"
                          }}
                          a
                        </h3>
                        <h3 class="fw-bold">
                          #{{ box_to_modal.box.number }} lakatszekrény kulcsát
                        </h3>
                      </div>
                      <template #modal-footer="{ cancel, ok }">
                        <b-button variant="default" @click="cancel()">
                          Mégse
                        </b-button>
                        <PinSignatureSimple
                          v-if="box_to_modal.user"
                          :user_id="box_to_modal.user.id"
                          @signed="ok()"
                          :hideSignButton="!box_to_modal.user.pin_set"
                        >
                        </PinSignatureSimple>
                      </template>
                    </b-modal>
                    <b-modal id="box-lock-return" @ok="returnBoxLockSend">
                      <template slot="modal-header">
                        <h4 class="modal-title">
                          <Loading
                            mini
                            v-if="!lock_return.box || !lock_return.color"
                          ></Loading>
                          <span v-else>
                            #{{
                              lock_return.color == 4
                                ? lock_return.box.black_lock_name
                                : lock_return.blue_lock_name
                            }}
                          </span>
                          Lakat visszahozás
                        </h4>
                        <a class="btn-close"></a>
                      </template>
                      <Loading v-if="!lock_return.user"></Loading>
                      <div v-else>
                        <label class="form-label"> Lakatot visszahozta: </label>
                        <v-select
                          v-model="lock_return.user"
                          :options="
                            lock_return.color == 4
                              ? partner_users
                              : ddc_employees
                          "
                          label="name"
                        >
                          <template #search="{ attributes, events }">
                            <input
                              class="vs__search"
                              v-bind="attributes"
                              v-on="events"
                              :placeholder="
                                lock_return.user == null ? 'Válassz' : ''
                              "
                            />
                          </template>
                        </v-select>
                      </div>
                      <template #modal-footer="{ cancel, ok }">
                        <b-button variant="default" @click="cancel()">
                          Mégse
                        </b-button>
                        <PinSignatureSimple
                          v-if="lock_return.user"
                          :text="
                            lock_return.color == 1
                              ? 'Kijelentem, hogy a munkavégzéssel kapcsolatos berendezés sor funkció próbáját elvégeztem. A biztonságos és rendeltetésszerű üzemeltetésre alkalmas.'
                              : 'A gép, gépsor, berendezés karbantartását, javítását, nagyjavítását elvégeztük. A biztonságos és rendeltetésszerű üzemeltetésre alkalmas.'
                          "
                          :user_id="lock_return.user.id"
                          @signed="ok()"
                          :hideSignButton="!lock_return.user.pin_set"
                        ></PinSignatureSimple>
                      </template>
                    </b-modal>
                  </div>
                  <!-- <div class="text-end">
										<button class="btn btn-success" @click="saveTaskBoxes"><i class="fa fa-save"></i> Mentés</button>
									</div> -->
                </div>
                <div v-else>
                  <table class="table table-hover table-bordered">
                    <thead>
                      <tr>
                        <th>Megnevezés</th>
                        <th>Lakat</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(position, id) in positions_sorted" :key="id">
                        <td class="align-middle">
                          {{ position.position_simple.name }} -
                          {{ position.position_simple.position_id }}
                        </td>
                        <td class="align-middle text-end">
                          <Loading mini v-if="!free_locks"></Loading>
                          <Loading mini v-else-if="!ddc_user"></Loading>
                          <SelectLock
                            :user="ddc_user"
                            :color="1"
                            @lock_selected="lockSelected($event, 1)"
                            :button_disabled="
                              user.id != task.operator_id || user.role_id != 7
                            "
                            :locks="blue_locks"
                            :position_id="position.id"
                            v-else-if="
                              ((position.locking && !task.partner_user_id) ||
                                (task.partner_user_id &&
                                  position.locking &&
                                  position.unplug)) &&
                              !position.locks.find((l) => l.color == 1) &&
                              task.status < 50
                            "
                          >
                          </SelectLock>
                          <span
                            class="badge bg-blue"
                            v-else-if="
                              ((position.locking && !task.partner_user_id) ||
                                (task.partner_user_id &&
                                  position.locking &&
                                  position.unplug)) &&
                              !position.locks.find((l) => l.color == 1) &&
                              task.status >= 50
                            "
                            ><i class="fa fa-check"></i> lakat visszahozva</span
                          >
                          <span
                            class="input-group"
                            v-else-if="
                              (position.locking && !task.partner_user_id) ||
                              (task.partner_user_id &&
                                position.locking &&
                                position.unplug)
                            "
                          >
                            <button
                              class="input-group-text bg-white"
                              :disabled="loading == position.id"
                              v-if="
                                user.id == task.operator_id &&
                                user.role_id == 7 &&
                                task.status < 50
                              "
                              @click="freeLock(position, 1)"
                            >
                              <i
                                v-if="loading == position.id"
                                class="fa fa-cog fa-2x fa-spin"
                              ></i>
                              <i
                                v-else
                                class="fa fa-delete-left fa-2x text-red"
                              ></i>
                            </button>
                            <button
                              class="input-group-text bg-white"
                              :disabled="loading == position.id"
                              v-else-if="
                                user.id == task.operator_id &&
                                user.role_id == 7 &&
                                (task.status < 50 || task.status == 60)
                              "
                              @click="returnLock(position, 1)"
                            >
                              <i
                                v-if="loading == position.id"
                                class="fa fa-cog fa-2x fa-spin"
                              ></i>
                              <i
                                v-else
                                class="fa fa-arrow-rotate-right fa-2x text-warning"
                              ></i>
                            </button>
                            <span class="form-control"
                              ><i class="fa fa-lock fa-2x text-blue"></i> #{{
                                position.locks.find((l) => l.color == 1).name
                              }}</span
                            >
                          </span>
                          <SelectLock
                            :user="black_lock_user"
                            :color="4"
                            @lock_selected="lockSelected($event, 4)"
                            :button_disabled="
                              user.id != task.operator_id || user.role_id != 7
                            "
                            :locks="black_locks"
                            btnClass="bg-black-strict ms-2"
                            :position_id="position.id"
                            v-if="
                              task.partner_user_id &&
                              position.locking &&
                              !position.locks.find((l) => l.color == 4) &&
                              task.status < 50
                            "
                          >
                          </SelectLock>
                          <span
                            class="badge bg-black-strict"
                            v-else-if="
                              task.partner_user_id &&
                              position.locking &&
                              !position.locks.find((l) => l.color == 4) &&
                              task.status >= 50
                            "
                            ><i class="fa fa-check"></i> lakat visszahozva</span
                          >
                          <span
                            class="input-group"
                            v-else-if="position.locking && task.partner_user_id"
                          >
                            <button
                              class="input-group-text bg-white"
                              :disabled="loading == position.id"
                              v-if="
                                user.id == task.operator_id &&
                                user.role_id == 7 &&
                                task.status < 50
                              "
                              @click="freeLock(position, 4)"
                            >
                              <i
                                v-if="loading == position.id"
                                class="fa fa-cog fa-2x fa-spin"
                              ></i>
                              <i
                                v-else
                                class="fa fa-delete-left fa-2x text-red"
                              ></i>
                            </button>
                            <button
                              class="input-group-text bg-white"
                              :disabled="loading == position.id"
                              v-else-if="blackLockReadyForReturn()"
                              @click="returnLock(position, 4)"
                            >
                              <i
                                v-if="loading == position.id"
                                class="fa fa-cog fa-2x fa-spin"
                              ></i>
                              <i
                                v-else
                                class="fa fa-arrow-rotate-right fa-2x text-warning"
                              ></i>
                            </button>
                            <span class="form-control"
                              ><i
                                class="fa fa-lock fa-2x text-black-strict"
                              ></i>
                              #{{
                                position.locks.find((l) => l.color == 4).name
                              }}</span
                            >
                          </span>
                          <span
                            v-if="position.locking == 0"
                            class="badge bg-default text-dark"
                            ><i class="fa fa-ban"></i> Nem igényel
                            lakatolást</span
                          >
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="w-100 text-end" v-if="task.started_at">
                    <button class="btn btn-green" disabled>
                      <i class="fa fa-check"></i> Elindítva
                    </button>
                  </div>
                </div>
                <b-modal
                  id="lock-return"
                  @close="lockReturnClose()"
                  @ok="returnLockSend"
                >
                  <template slot="modal-header">
                    <h4 class="modal-title">
                      <Loading mini v-if="!lock_return.position"></Loading>
                      <span v-else>
                        #{{
                          lock_return.position.locks.find(
                            (l) => l.color == lock_return.color
                          ).name
                        }}
                      </span>
                      Lakat visszahozás
                    </h4>
                    <a class="btn-close" @click="lockReturnClose()"></a>
                  </template>
                  <Loading v-if="!lock_return.user"></Loading>
                  <div v-else>
                    <label class="form-label"> Lakatot visszahozta: </label>
                    <v-select
                      v-model="lock_return.user"
                      :options="
                        lock_return.color == 4 ? partner_users : ddc_employees
                      "
                      label="name"
                      :clearable="false"
                    >
                      <template #search="{ attributes, events }">
                        <input
                          class="vs__search"
                          v-bind="attributes"
                          v-on="events"
                          :placeholder="
                            lock_return.user == null ? 'Válassz' : ''
                          "
                        />
                      </template>
                    </v-select>
                  </div>
                  <template #modal-footer="{ cancel, ok }">
                    <b-button variant="default" @click="cancel()">
                      Mégse
                    </b-button>
                    <PinSignatureSimple
                      v-if="lock_return.user"
                      :text="
                        lock_return.color == 1
                          ? 'Kijelentem, hogy a munkavégzéssel kapcsolatos berendezés sor funkció próbáját elvégeztem. A biztonságos és rendeltetésszerű üzemeltetésre alkalmas.'
                          : 'A gép, gépsor, berendezés karbantartását, javítását, nagyjavítását elvégeztük. A biztonságos és rendeltetésszerű üzemeltetésre alkalmas.'
                      "
                      :user_id="lock_return.user.id"
                      @signed="ok()"
                      :hideSignButton="!lock_return.user.pin_set"
                    ></PinSignatureSimple>
                  </template>
                </b-modal>
                <template #overlay>
                  <div class="text-center">
                    <h3><i class="fa fa-exclamation-triangle"></i></h3>
                    <strong>Nem te vagy a hozzárendelt operátor</strong>
                  </div>
                </template>
              </b-overlay>
            </div>
          </div>
        </panel>
        <panel title="Csatolt dokumentumok">
          <ImageUpload parent_model="Task" :model_id="task.id"></ImageUpload>
        </panel>
      </div>
    </div>
  </div>
</template>

<script>
import Loading from "../../components/Loading";
import NoRecords from "../../components/NoRecords";
import PinSignature from "../../components/PinSignature";
import PinSignatureSimple from "../../components/PinSignatureSimple";
import SelectLock from "../../components/SelectLock";
import Task from "../../apis/Task";
import Validity from "../../apis/Validity";
import User from "../../apis/User";
import Partner from "../../apis/Partner";
import Box from "../../apis/Box";
import Workflow from "../../apis/Workflow";
import Confirmation from "../../apis/Confirmation";
import Lock from "../../apis/Lock";
import PermitForm1 from "../../components/permit-forms/PermitForm1";
import PermitForm2 from "../../components/permit-forms/PermitForm2";
import PermitForm3 from "../../components/permit-forms/PermitForm3";
import PermitForm4 from "../../components/permit-forms/PermitForm4";
import IsolationCard from "../../components/IsolationCard";
import ImageUpload from "../../components/ImageUpload";
import moment from "moment";
import RisksIcon from "../../components/RisksIcon";
import HandoverPopover from "../../components/HandoverPopover";
import CranePanel from "./partials/CranePanel";
import DetailsPanel from "./partials/DetailsPanel";
import IsolationsPanel from "./partials/IsolationsPanel";
import AreaPermit from "./partials/AreaPermit";

export default {
  components: {
    Loading,
    NoRecords,
    PinSignature,
    PinSignatureSimple,
    SelectLock,
    PermitForm1,
    PermitForm2,
    PermitForm3,
    PermitForm4,
    IsolationCard,
    ImageUpload,
    RisksIcon,
    HandoverPopover,
    CranePanel,
    DetailsPanel,
    IsolationsPanel,
    AreaPermit,
  },
  data() {
    return {
      task: false,
      users: false,
      errors: [],
      permit_types: false,
      permit_local_roles: false,
      roles: false,
      security_types: false,
      task_statuses: false,
      permit_to_add: {},
      permit_type_to_add: {},
      loading: false,
      permit_form: false,
      workflow: false,
      partner: false,
      partner_users: false,
      boxes: false,
      units: false,
      free_locks: false,
      task_positions: false,
      task_permit_types: false,
      isolations: false,
      locks: false,
      task_boxes: false,
      boxes_to_send: [],
      back_url: false,
      comment: "",
      start_date: null,
      start_time: null,
      end_date: null,
      end_time: null,
      with_time: false,
      validity_records: false,
      is_task_positions_dirty: false,
      location_leads: false,
      ddc_user: false,
      black_lock_user: {},
      abandonment_values: {
        isolated: true,
        secure: 1,
      },
      acceptance_val: 1,
      abandonment_radio_options: [],
      acceptance_radio_options: [],
      abandonment_radio_options_high: [],
      position_to_add: null,
      lock_return: { user: null },
      box_to_modal: { user: null },
      set_closable_errors: [],
      task_extension: {
        end_date: moment().format("YYYY-MM-DD"),
        end_time: null,
        with_time: false,
      },
      now: moment().format("YYYY-MM-DD"),
      decline_location_leader_id: null,
      task_comments: false,
      permit_statuses: false,
      areaPermitForm: {},
      third_party_partner_workers: false,
      partners: false,
      hasBlackLocksReturned: null,
    };
  },
  computed: {
    areaPermits() {
      if (this.task_permit_types) {
        return this.task_permit_types.filter((pt) => pt.permit_type == 5);
      } else {
        return [];
      }
    },
    activeAreaPermit() {
      if (this.task_permit_types) {
        return this.task_permit_types.find(
          (pt) => pt.permit_type == 5 && pt.status < 90
        );
      } else {
        return false;
      }
    },
    positions() {
      return this.$store.state.positions;
    },
    userless_permit_count() {
      if (this.task_permit_types) {
        return this.task_permit_types.filter((pt) => pt.permits.length == 0)
          .length;
      } else {
        return 0;
      }
    },
    incomplete_narrow_permit_count() {
      if (this.task_permit_types) {
        let permit_types = this.task_permit_types.filter(
          (pt) => pt.permit_type == 2
        );
        if (permit_types.length > 0) {
          return permit_types.filter(
            (pt) =>
              pt.permits.filter((pm) => pm.local_role == 2).length == 0 ||
              pt.permits.filter((pm) => pm.local_role == 3).length == 0
          ).length;
        } else {
          return 0;
        }
      } else {
        return 0;
      }
    },
    filtered_permit_types_array() {
      if (this.task && this.permit_types) {
        if (this.task.partner_id) {
          if (this.user.role_id == 5) {
            return this.permit_types
              .filter((pt) => pt.id != 5)
              .map((m) => ({
                id: m.id,
                name: m.short_name,
              }));
          } else {
            return this.permit_types
              .filter((pt) => ![1, 5].includes(pt.id))
              .map((m) => ({
                id: m.id,
                name: m.short_name,
              }));
          }
        } else {
          if (this.user.role_id == 5) {
            return this.permit_types
              .filter((pt) => ![3, 5].includes(pt.id))
              .map((m) => ({
                id: m.id,
                name: m.short_name,
              }));
          } else {
            return this.permit_types
              .filter((pt) => ![1, 3, 5].includes(pt.id))
              .map((m) => ({
                id: m.id,
                name: m.short_name,
              }));
          }
        }
      } else {
        return [];
      }
    },
    user() {
      return this.$store.state.user;
    },
    /*types_in_task_permits(){
			if(this.task){
				return [...new Set(this.task.task_permits.map(item => item.permit_type))]
			}else{
				return [];
			}
		}*/
    positions_sorted() {
      if (!this.task_positions) {
        return [];
      } else {
        return JSON.parse(JSON.stringify(this.task_positions)).sort((a, b) => {
          return (
            (a.position_simple.is_high_voltage -
              b.position_simple.is_high_voltage) *
            -1
          );
        });
      }
    },
    position_simple_array() {
      let obj = JSON.parse(JSON.stringify(this.task_positions));
      if (obj) {
        return obj.map((p) => ({
          position_id: p.position_id,
          locking: p.locking,
          unplug: p.unplug,
        }));
      } else {
        return [];
      }
    },
    blue_locks() {
      if (this.free_locks) {
        return this.free_locks.filter((l) => l.color == 1);
      }
      return [];
    },
    black_locks() {
      if (this.free_locks) {
        return this.free_locks.filter((l) => l.color == 4);
      }
      return [];
    },
    normal_permits() {
      return this.task_permit_types.filter((t) => t.permit_type != 5);
    },
    electricians() {
      if (this.users) {
        return this.users
          .filter((u) => [8, 9].includes(u.original_role_id))
          .map((u) => ({
            id: u.id,
            name:
              u.last_name +
              " " +
              u.first_name +
              (u.name_extension != null ? " " + u.name_extension : ""),
            pin_set: u.pin_set,
          }));
      }
      return [];
    },
    electricians_high_voltage() {
      if (this.users) {
        return this.users
          .filter((u) => u.original_role_id == 9)
          .map((u) => ({
            id: u.id,
            name:
              u.last_name +
              " " +
              u.first_name +
              (u.name_extension != null ? " " + u.name_extension : ""),
            pin_set: u.pin_set,
          }));
      }
      return [];
    },
    project_leaders() {
      if (this.users) {
        return this.users
          .filter((u) => u.roles.includes(5))
          .map((u) => ({
            id: u.id,
            name:
              u.last_name +
              " " +
              u.first_name +
              (u.name_extension != null ? " " + u.name_extension : ""),
            pin_set: u.pin_set,
          }));
      }
      return [];
    },
    ddc_users() {
      if (this.users) {
        return this.users
          .filter((u) => u.roles.includes(10))
          .map((u) => ({
            id: u.id,
            name:
              u.last_name +
              " " +
              u.first_name +
              (u.name_extension != null ? " " + u.name_extension : ""),
            pin_set: u.pin_set,
          }));
      }
      return [];
    },
    ddc_employees() {
      if (this.users) {
        return this.users
          .filter((u) => ![6, 11].includes(u.role_id))
          .map((u) => ({
            id: u.id,
            name:
              u.last_name +
              " " +
              u.first_name +
              (u.name_extension != null ? " " + u.name_extension : ""),
            pin_set: u.pin_set,
          }));
      }
      return [];
    },
    isolation_send_array() {
      if (this.isolations) {
        return this.isolations
          .filter((i) => i.user_id != null)
          .map((fi) => ({ id: fi.id, user_id: fi.user_id }));
      }
      return [];
    },
    hasRadiatingUnits() {
      return this.task.boxes.filter((u) => u.is_radiating == "1").length > 0;
    },
    isMatchingUser() {
      return (
        (this.user.role_id == 5 && this.task.owner_id == this.user.id) ||
        (this.user.role_id == 3 && this.task.location_leader_id == this.user.id)
      );
    },
    roles_available_for_permit() {
      if (typeof this.permit_to_add.permit_type == "undefined") {
        return [];
      }
      let roles = this.permit_local_roles;
      if (this.permit_to_add.permit_type == 4) {
        roles = roles.filter((u) => u.id != 2);
      }
      if (this.permit_to_add.permit_type == 1) {
        roles = roles.filter((u) => u.id == 1);
      }
      if (this.permit_to_add.permit_type == 3) {
        roles = roles.filter((u) => u.id == 3);
      }

      return roles;
    },
  },
  watch: {
    $route() {
      this.refalser();
      this.initializer();
    },
    "areaPermitForm.coordination_partner_id": function (val) {
      if (val) {
        this.third_party_partner_workers = false;
        User.getRole(6).then((response) => {
          if (response.data.length != 0) {
            this.third_party_partner_workers = response.data.filter(
              (item) => item.partner_id == val
            );
            this.third_party_partner_workers.forEach((tppw) => {
              this.$set(
                tppw,
                "name",
                tppw.last_name +
                  " " +
                  tppw.first_name +
                  (tppw.name_extension != null
                    ? " " + tppw.name_extension
                    : "") +
                  (tppw.partner_id != null
                    ? " (" + tppw.partner_name + ")"
                    : "")
              );
            });
          } else {
            this.third_party_partner_workers = response.data;
          }
        });
      }
    },
    /*task_positions:{
			handler(val,prev){
				if(prev != false){
					this.is_task_positions_dirty = true;
				}
			},
			deep: true
		},
		is_task_positions_dirty(val){
			console.log('is_dirty: '+val);
		}*/
  },
  mounted() {
    this.back_url = process.env.VUE_APP_BACK_URL;
  },
  created() {
    this.roles = JSON.parse(localStorage.getItem("constants")).roles;
    this.permit_statuses = JSON.parse(
      localStorage.getItem("constants")
    ).permit_status;
    Partner.index().then((response) => {
      this.partners = response.data;
    });
    this.initializer();
  },
  methods: {
    updateTaskPositionDirty(value) {
      this.is_task_positions_dirty = value;
    },
    updateTaskPositions() {
      this.$forceUpdate(this.task_positions);
    },
    updateDdcUser(ddc_user) {
      this.ddc_user = ddc_user;
    },
    updateBlackLockUser(black_lock_user) {
      this.black_lock_user = black_lock_user;
    },
    taskParameterUpdated(parameter_object) {
      Object.assign(this.task, parameter_object);
    },
    hasWatcherAndLeader(task_permit_type) {
      return (
        task_permit_type.permits.find((p) => p.local_role == 3) &&
        task_permit_type.permits.find((p) => p.local_role == 2)
      );
    },
    hasWatcherAndAnyone(task_permit_type) {
      return (
        task_permit_type.permits.find((p) => p.local_role == 3) &&
        task_permit_type.permits.find((p) => p.local_role != 3)
      );
    },
    signReturn(task_permit_log_id) {
      User.permitTypeReturnSign({
        task_permit_log_id: task_permit_log_id,
      }).then(() => {
        this.showPermitTypes();
      });
    },
    signLoggedIn(task_permit) {
      Task.signLoggedIn({ taskpermit_id: task_permit.id }).then((response) => {
        this.$set(task_permit, "signed_at", response.data);
      });
    },
    deletePermitType(task_permit_type_id) {
      Confirmation.swalCustomConfirm(
        "Biztos törlöd az engedélyt?",
        "Törlés",
        "A törlés az engedélyen lévő összes munkavállalót is törli"
      ).then((response) => {
        if (response.isConfirmed === true) {
          Task.deletePermitType(task_permit_type_id).then(() => {
            this.task_permit_types.splice(
              this.task_permit_types.findIndex(
                (tp) => tp.id == task_permit_type_id
              ),
              1
            );
          });
        }
      });
    },
    getUserSign(task_permit_type) {
      Task.getUserSign(task_permit_type.id).then((response) => {
        if (response.data) {
          //console.log(task_permit_type.permits.find(u => u.user_id == response.data.user_to_sign));
          this.loading = true;
          task_permit_type.user_sign = response.data;
          task_permit_type.return_user_to_sign = task_permit_type.permits.find(
            (u) => u.user_id == response.data.user_to_sign
          );
          this.loading = false;
        }
      });
    },
    sendForSign(task_permit_type, return_data) {
      Task.sendForSign(task_permit_type.id, {
        user_id: task_permit_type.return_user_to_sign.user_id,
        return_data: return_data,
      }).then(() => {
        //console.log(response.data);
        this.$bvModal.hide("fill_abandonment_" + task_permit_type.id);
      });
    },
    bringBackHandler(task_permit_type) {
      if (task_permit_type.permits.filter((p) => !p.signed_at).length == 0) {
        this.$bvModal.show("fill_abandonment_" + task_permit_type.id);
      } else {
        Confirmation.swalErrorMessage(
          "Csak akkor hozhatja vissza az engedélyt ha mindenki aláírta"
        );
      }
    },
    permitToAddInitializer(task_permit_type) {
      this.permit_to_add = {};
      this.permit_to_add.task_id = this.task.id;
      this.permit_to_add.permit_type = task_permit_type.permit_type;
      this.permit_to_add.permit_type_id = task_permit_type.id;
    },
    SendExtraComment() {
      Task.appendComment(this.task.id, { comment: this.extra_comment }).then(
        () => {
          this.extra_comment = null;
        }
      );
    },
    cancelBoxKeyModal() {
      let box = this.task_boxes.find((b) => b.id == this.box_to_modal.box.id);
      if (this.task.status < 50) {
        if (box.key_at_task) {
          this.$set(box, "key_at_task", false);
        } else {
          this.$set(box, "key_at_task", true);
        }
      } else {
        this.$set(box, "key_at_task", this.box_to_modal.box.key_at_task);
      }
      this.box_to_modal = { user: null };
    },
    confirmThenMove(permit_type) {
      new Promise((resolve) => {
        let text = "";
        let title = "";
        switch (permit_type.permit_type) {
          case 1:
            title = "Kijelentem hogy";
            if (permit_type.status == 70) {
              text =
                "A tűzveszélyes tevékenység befejezését követően a munkavégzés területét tűzvédelmi szempontból átvizsgáltam, az esetleges veszélyeket megszüntettem, a tevékenységet készre jelentem";
            }
            if (permit_type.status == 80) {
              text =
                "A tevékenység befejezését tudomásul vettem, átadásakor hiányosságot nem tapasztaltam";
            }
            break;
          case 2:
            if (permit_type.status == 30) {
              title = "A fent megnevezett munkafolyamatot engedélyezem!";
              text =
                "Az adott helyszínen történő biztonságos munkavégzéshez szükséges speciális, lokális ismeretek, utasítások az elrendelővel ismertetve lettek. A berendezésbe történő anyagbejutás megakadályozása megtörtént.";
            }
            break;
          case 3:
            if (permit_type.status == 30) {
              title = "A fent megnevezett munkafolyamatot engedélyezem!";
              text =
                "Az adott helyszínen történő biztonságos munkavégzéshez szükséges speciális, lokális ismeretek, utasítások az elrendelővel ismertetve lettek. A berendezésbe történő anyagbejutás megakadályozása megtörtént.";
            }
            break;
          case 4:
            if (permit_type.status == 30) {
              title = "A fent megnevezett munkafolyamatot engedélyezem!";
              text =
                "Megfelelő megelőző intézkedésekkel biztosított a biztonságos munkavégzés kivéve, ha ez a munkavégzés közben vihető véghez.";
            }
        }
        resolve({ text: text, title: title });
      }).then((response) => {
        Confirmation.swalCustomConfirm(
          response.title,
          "Igen",
          response.text
        ).then((response) => {
          if (response.isConfirmed === true) {
            this.loading = true;
            Task.movePermitStatus(permit_type.id).finally(() => {
              this.loading = false;
            });
          }
        });
      });
    },
    validityActiveUpdate(validity_id, active_val) {
      Validity.validityActiveUpdate(validity_id, { active: active_val }).then(
        (response) => {
          this.validity_records = response.data;
        }
      );
    },
    getValidityRecords(id) {
      this.validity_records = false;
      Task.getTaskPermitTypeValidityRecords(this.task.id, id).then(
        (response) => {
          this.validity_records = response.data;
        }
      );
    },
    getTaskValidityRecords() {
      this.validity_records = false;
      Task.getTaskValidityRecords(this.task.id).then((response) => {
        this.validity_records = response.data;
      });
    },
    getSlicedDate(datetime) {
      let m = moment(datetime);
      if (
        (m.hour() == 0 && m.minute() == 0) ||
        (m.hour() == 23 && m.minute() == 59)
      ) {
        return m.format("yyyy-MM-DD");
      } else {
        return m.format("yyyy-MM-DD HH:mm");
      }
    },
    diffLessThanThreeDays(datetime) {
      return moment.duration(moment(datetime).diff(moment())).asHours() <= 72;
    },
    extendPermit(id) {
      let end_date_time = this.end_date
        ? moment(
            this.end_date +
              (this.end_time && this.with_time ? " " + this.end_time : " 23:59")
          )
            .tz("Europe/Budapest")
            .format()
        : this.end_date;
      Task.permitExtend(this.task.id, id, {
        end_date_time: end_date_time,
      }).then(() => {
        this.task_permit_types.find(
          (t) => t.id == id
        ).has_unresolved_validity = true;
      });
    },
    pdfButtonHandler(task_permit_type) {
      if (task_permit_type.status == 50) {
        this.$set(
          this.task_permit_types[
            this.task_permit_types.findIndex((i) => i.id == task_permit_type.id)
          ],
          "accepted",
          true
        );
        Task.movePermitStatus(task_permit_type.id)
          .catch(() => {
            this.task_permit_types[
              this.task_permit_types.findIndex(
                (i) => i.id == task_permit_type.id
              )
            ].accepted = false;
          })
          .then(() => {
            window.open(
              this.back_url + "/document/pdf/permit/" + task_permit_type.id,
              "_blank"
            );
          });
      } else {
        window.open(
          this.back_url + "/document/pdf/permit/" + task_permit_type.id,
          "_blank"
        );
      }
    },
    movePermitStatus(id, data) {
      let selected_task_permit_type = this.task_permit_types.find(
        (tpt) => tpt.id == id
      );
      this.$set(
        this.task_permit_types[
          this.task_permit_types.findIndex((i) => i.id == id)
        ],
        "accepted",
        true
      );
      //console.log(this.task_permit_types[this.task_permit_types.findIndex(i => i.id == id)].accepted);
      this.loading = true;
      Task.movePermitStatus(id, data)
        .then(() => {
          if (
            [2].includes(selected_task_permit_type.permit_type) &&
            selected_task_permit_type.status == 80 &&
            this.user.id == this.task.location_leader_id &&
            this.user.role_id == 3
          ) {
            this.$bvModal.show("fill_acceptance" + id);
          }
        })
        .catch(() => {
          this.task_permit_types[
            this.task_permit_types.findIndex((i) => i.id == id)
          ].accepted = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setSigned(task_permit, event) {
      this.$set(task_permit, "signed_at", event.signed_at);
    },
    refalser() {
      window.Echo.private("App.Task." + this.task.id).unsubscribe();
      /*	window.Echo.channel('isolations').unsubscribe();
				window.Echo.channel('locks').unsubscribe();
				window.Echo.channel('boxes').unsubscribe();*/
      this.task = false;
      this.users = false;
      this.errors = [];
      this.permit_types = false;
      this.permit_local_roles = false;
      this.security_types = false;
      this.task_statuses = false;
      this.permit_to_add = {};
      this.loading = false;
      this.permit_form = false;
      this.workflow = false;
      this.partner = false;
      this.partner_users = false;
      this.boxes = false;
      this.units = false;
      this.free_locks = false;
      this.task_positions = false;
      this.task_comments = false;
      this.task_permit_types = false;
      this.isolations = false;
      this.locks = false;
      this.task_boxes = false;
      this.boxes_to_send = [];
      this.lock_return = { user: null };
      this.box_to_modal = { user: null };
    },
    initializer() {
      window.Echo.private("App.Task." + this.$route.params.id)
        .listen("PermitSigned", (e) => {
          this.$set(
            this.task_permit_types
              .find((tpt) => tpt.id == e.task_permit_type_id)
              .permits.find((p) => p.id == e.taskpermit_id),
            "signed_at",
            e.signed_at
          );
        })
        .listen("TaskEditUpdated", (e) => {
          Task.show(e.id)
            .then((response) => {
              this.$set(this, "task", response.data);
            })
            .catch(() => {
              this.$set(this, "task", { name: "ismeretlen", notfound: true });
            });
        })
        .listen("TaskPositions", (e) => {
          Task.showTaskPositions(e.id)
            .then((response) => {
              this.task_positions = response.data;

              this.task_positions.forEach((p) => {
                if (p.position_simple.is_high_voltage == 1 && p.box_id) {
                  Box.show(p.box_id).then((response) => {
                    p.box_name = response.data.name;
                  });
                }
              });
            })
            .finally(() => {
              this.is_task_positions_dirty = false;
            });
        })
        .listen("TaskPermits", (e) => {
          Task.showPermitTypes(e.id).then((response) => {
            this.task_permit_types = response.data;
          });
        })
        .listen("TaskPermitUpdated", (e) => {
          let taskPermitTypeIndex = this.task_permit_types.findIndex(
            (i) => i.id == e.id
          );
          if (taskPermitTypeIndex != -1) {
            this.task_permit_types[taskPermitTypeIndex].status = e.status;
            this.task_permit_types[
              taskPermitTypeIndex
            ].has_unresolved_validity = e.has_unresolved_validity;
            this.task_permit_types[taskPermitTypeIndex].validity = e.validity;
            this.task_permit_types[taskPermitTypeIndex].end_date_time =
              e.end_date_time;
            this.$forceUpdate(this.task_permit_types);
          }
        })
        .listen("TaskIsolationCreated", (e) => {
          this.isolations.push(e);
        })
        .listen("TaskBoxUpdated", (e) => {
          this.task_boxes[this.task_boxes.findIndex((tb) => tb.id == e.id)] = e;
          this.$forceUpdate(this.task_boxes);
        })
        .listen("TaskPermitTypeAdded", (e) => {
          console.log(e);
        })
        .listen("TaskCommentAdded", (e) => {
          this.task_comments.unshift(e[0]);
        });

      window.Echo.channel("isolations").listen("IsolationUpdated", (e) => {
        console.log("itt vagyunk", e.id);
        this.isolations[this.isolations.findIndex((i) => i.id == e.id)] = e;
        this.$forceUpdate(this.isolations);
      });

      window.Echo.channel("locks")
        .listen("SetLock", (e) => {
          this.free_locks.splice(
            this.free_locks.map((fl) => fl.id).indexOf(e.id),
            1
          );
        })
        .listen("LockRelease", (e) => {
          this.free_locks.push(e);
          this.free_locks = this.free_locks.sort((a, b) => a.id > b.id);
        });

      window.Echo.channel("boxes").listen("TaskBox", (e) => {
        console.log(e);
        this.task_boxes[
          this.task_boxes.findIndex((tb) => tb.id == e.id)
        ].key_at_task = e.key_at_task;
        this.$forceUpdate(this.task_boxes);
      });

      Lock.free().then((response) => {
        this.free_locks = response.data;
      });

      Task.show(this.$route.params.id)
        .then((response) => {
          this.$set(this, "task", response.data);

          //ez a rész eddig a Tasj.show -n kívül volt, de bemozgattam ide alá, hogy megfelően tudjuk kezelni a task 404-et.
          this.showTaskPositions();
          this.showPermitTypes();

          Task.showIsolations(this.$route.params.id).then((response) => {
            this.isolations = response.data;
          });

          Task.showComments(this.$route.params.id).then((response) => {
            this.task_comments = response.data;
          });

          Task.showBoxes(this.$route.params.id).then((response) => {
            this.task_boxes = response.data;
          });

          if (response.data.ddc_user_id) {
            User.show(response.data.ddc_user_id).then((response) => {
              this.ddc_user = response.data;
            });
          }
          if (response.data.black_lock_user_id) {
            User.show(response.data.black_lock_user_id).then((response) => {
              this.black_lock_user = response.data;
            });
          } else {
            this.black_lock_user = false;
          }
          if (response.data.partner_id) {
            User.getRoles([6, 11]).then((response) => {
              if (response.data.length != 0) {
                this.partner_users = response.data.filter(
                  (item) => item.partner_id == this.task.partner_id
                );
              } else {
                this.partner_users = response.data;
              }
            });
            Partner.show(response.data.partner_id).then((response) => {
              this.partner = response.data;
            });
          }
          if (response.data.workflow_id) {
            Workflow.forceShow(response.data.workflow_id).then((response) => {
              this.workflow = response.data;
            });
          }
          /*if(this.user.role_id == 7){
						Task.setOperator(this.task.id,{operator_id: this.user.id});
					}*/
          //console.log([...new Set(this.task.task_permits.map(item => item.permit_type))])
        })
        .catch(() => {
          this.$set(this, "task", { name: "ismeretlen", notfound: true });
        });

      User.index().then((response) => {
        this.users = response.data;
      });

      Box.index().then((response) => {
        this.boxes = response.data;
      });
      Box.units().then((response) => {
        this.units = response.data;
      });
      this.positions.forEach((p) => {
        this.$set(p, "full_name", p.position_id + " - " + p.name);
      });

      User.getRole(3).then((response) => {
        this.location_leads = response.data;
      });

      this.permit_types = JSON.parse(
        localStorage.getItem("constants")
      ).permit_types;
      this.permit_local_roles = JSON.parse(
        localStorage.getItem("constants")
      ).permit_local_roles;
      this.security_types = JSON.parse(
        localStorage.getItem("constants")
      ).security_types;
      this.task_statuses = JSON.parse(
        localStorage.getItem("constants")
      ).task_status;
      this.abandonment_radio_options = JSON.parse(
        localStorage.getItem("constants")
      ).abandonment_radio_options;
      this.acceptance_radio_options = JSON.parse(
        localStorage.getItem("constants")
      ).acceptance_radio_options;
      this.abandonment_radio_options_high = JSON.parse(
        localStorage.getItem("constants")
      ).abandonment_radio_options_high;

      /*console.log(
				this.permit_types,
				this.permit_local_roles,
				this.security_types,
				this.task_statuses,
				this.abandonment_radio_options,
				this.acceptance_radio_options,
				this.abandonment_radio_options_high,
			);*/
    },
    showTaskPositions() {
      Task.showTaskPositions(this.$route.params.id).then((response) => {
        this.task_positions = response.data;

        this.task_positions.forEach((p) => {
          if (p.position_simple.is_high_voltage == 1 && p.box_id) {
            Box.show(p.box_id).then((response) => {
              p.box_name = response.data.name;
            });
          }
        });
      });
    },
    showPermitTypes() {
      Task.showPermitTypes(this.$route.params.id).then((response) => {
        this.task_permit_types = response.data;
      });
    },
    userIdentifierByColor(color) {
      let vue = this;
      return new Promise((resolve) => {
        let user_id = null;
        switch (color) {
          case 1:
            user_id = vue.task.ddc_user_id;
            break;
          case 4:
            user_id = vue.task.partner_user_id;
            break;
        }
        resolve({ user_id: user_id });
      });
    },
    lockReturnClose() {
      this.$bvModal.hide("lock-return");
      this.lock_return = { user: null };
      this.$forceUpdate(this.lock_return);
    },
    returnLock(position, color) {
      this.userIdentifierByColor(color).then((response) => {
        this.lock_return.user = this.users.find(
          (u) => u.id == response.user_id
        );
        this.lock_return.position = position;
        this.lock_return.color = color;
        this.$forceUpdate(this.lock_return);
      });
      this.$bvModal.show("lock-return");
    },
    returnBoxLock(box, color) {
      this.userIdentifierByColor(color).then((response) => {
        this.lock_return.user = this.users.find(
          (u) => u.id == response.user_id
        );
        this.lock_return.box = box;
        this.lock_return.color = color;
        this.$forceUpdate(this.lock_return);
        this.$forceUpdate(this.lock_return);
        this.$forceUpdate(this.lock_return);
      });
      this.$bvModal.show("box-lock-return");
    },
    returnBoxLockSend() {
      Task.freeBoxLock(this.task.id, {
        box_id: this.lock_return.box.id,
        user_id: this.lock_return.user.id,
        lock_id:
          this.lock_return.color == 1
            ? this.lock_return.box.blue_lock_id
            : this.lock_return.box.black_lock_id,
      }).then((response) => {
        this.task_boxes[
          this.task_boxes.findIndex((b) => b.id == this.lock_return.box.id)
        ] = response.data;
        this.$forceUpdate(this.task_boxes);
      });
    },
    returnLockSend(vbModalEvt) {
      vbModalEvt.preventDefault();
      this.loading = this.lock_return.position.id;
      Task.freePositionLock(this.task.id, {
        task_position_id: this.lock_return.position.id,
        user_id: this.lock_return.user.id,
        lock_id: this.lock_return.position.locks.find(
          (l) => l.color == this.lock_return.color
        ).id,
      }).finally(() => {
        this.lockReturnClose();
        this.loading = false;
      });
    },
    freeLock(position, color) {
      Confirmation.swalCustomConfirm(
        "Biztos törlöd a lakatot?",
        "Törlöm",
        "A lakat törlése nem egyezik a visszahozással!"
      ).then((response) => {
        if (response.isConfirmed === true) {
          this.userIdentifierByColor(color).then((response) => {
            this.loading = position.id;
            Task.freePositionLock(this.task.id, {
              task_position_id: position.id,
              user_id: response.user_id,
              lock_id: position.locks.find((l) => l.color == color).id,
            }).finally(() => {
              this.loading = false;
            });
          });
        }
      });
    },
    deselectLock(box, color) {
      Confirmation.swalCustomConfirm(
        "Biztos törlöd a lakatot?",
        "Törlöm",
        "A lakat törlése nem egyezik a visszahozással!"
      ).then((response) => {
        if (response.isConfirmed === true) {
          let vue = this;
          new Promise((resolve) => {
            let user_id = null;
            let lock_id = null;
            switch (color) {
              case 1:
                user_id = vue.task.ddc_user_id;
                lock_id = box.blue_lock_id;
                break;
              case 4:
                user_id = vue.task.partner_user_id;
                lock_id = box.black_lock_id;
                break;
            }
            resolve({ user_id: user_id, lock_id: lock_id });
          }).then((response) => {
            Task.freeBoxLock(this.task.id, {
              box_id: box.id,
              user_id: response.user_id,
              lock_id: response.lock_id,
            }).then((response) => {
              this.task_boxes[
                this.task_boxes.findIndex((b) => b.id == box.id)
              ] = response.data;
              this.$forceUpdate(this.task_boxes);
            });
          });
        }
      });
    },
    boxLockSelected(box, event, color) {
      let vue = this;
      new Promise((resolve) => {
        let user_id = null;
        switch (color) {
          case 1:
            user_id = vue.task.ddc_user_id;
            break;
          case 4:
            user_id = vue.task.partner_user_id;
            break;
        }
        resolve({ user_id: user_id });
      }).then((response) => {
        Task.setBoxLock(this.task.id, {
          box_id: box.id,
          user_id: response.user_id,
          lock_id: event.lock_id,
        })
          .then((response) => {
            this.task_boxes[this.task_boxes.findIndex((b) => b.id == box.id)] =
              response.data;
          })
          .catch(() => {
            //console.log(this.$refs['black_lock_select_' + box.id][0]);
            this.$refs["black_lock_select_" + box.id][0].resetSignature();
          });
      });
    },
    saveTaskBoxes() {
      Task.setLocks(this.task.id, { boxes: this.boxes_to_send });
    },
    boxSendHandle() {
      if (this.box_to_modal.box.key_at_task === true) {
        Task.setBoxKey(this.task.id, {
          user_id: this.box_to_modal.user.id,
          box_id: this.box_to_modal.box.id,
        })
          .then((response) => {
            this.task_boxes.find(
              (b) => b.id == this.box_to_modal.box.id
            ).key_at_task = response.data.key_at_task;
          })
          .catch(() => {
            this.$set(this.box_to_modal.box, "key_at_task", false);
          })
          .finally(() => {
            this.box_to_modal = { user: null };
          });
      } else {
        Task.freeBoxKey(this.task.id, {
          user_id: this.box_to_modal.user.id,
          box_id: this.box_to_modal.box.id,
        })
          .then((response) => {
            this.task_boxes.find(
              (b) => b.id == this.box_to_modal.box.id
            ).key_at_task = response.data.key_at_task;
          })
          .catch(() => {
            this.$set(
              this.box_to_modal.box,
              "key_at_task",
              this.box_to_modal.box.key_at_task
            );
          })
          .finally(() => {
            this.box_to_modal = { user: null };
          });
      }
    },
    boxCheckHandle(box) {
      if (box.key_at_task) {
        this.box_to_modal.box = box;
        this.box_to_modal.user = this.ddc_user;
        this.$bvModal.show("sign-key");
      } else {
        if (this.task.status < 50) {
          Confirmation.swalCustomConfirm(
            "Biztos törlöd a kulcsot?",
            "Törlöm",
            "A kulcs törlése nem egyezik a visszahozással!"
          ).then((response) => {
            if (response.isConfirmed === true) {
              Task.freeBoxKey(this.task.id, {
                user_id: this.task.ddc_user_id,
                box_id: box.id,
              }).catch(() => {
                this.$set(box, "key_at_task", true);
              });
            } else {
              this.$set(box, "key_at_task", true);
            }
          });
        } else {
          this.box_to_modal = box;
          this.$bvModal.show("sign-key");
        }
      }
    },
    takeBackKey(box) {
      this.box_to_modal.box = box;
      this.box_to_modal.user = this.ddc_user;
      this.$forceUpdate(this.box_to_modal);
      this.$bvModal.show("sign-key");
      //Task.freeBoxKey(this.task.id,{user_id:this.task.ddc_user_id,box_id:box.id})
    },
    savePositionsLockingUnplug() {
      Task.positionUpdateToTask(this.task.id, {
        position_ids: this.position_simple_array,
      }).finally(() => {
        this.is_task_positions_dirty = false;
      });
    },
    checkPosition(position, btn_number) {
      this.$set(this, "is_task_positions_dirty", true);
      if (btn_number == 1 && position.unplug == 0) {
        this.$set(position, "locking", 1);
      } else if (btn_number == 2 && position.locking == 1) {
        this.$set(position, "unplug", 0);
      }
    },
    readyForEvaluation() {
      this.loading = true;
      Task.readyForEvaluation(this.task.id).finally(() => {
        this.loading = false;
        this.is_task_positions_dirty = false;
      });
    },
    undoEvaluationSend() {
      this.loading = true;
      Task.undoEvaluationSend(this.task.id).finally(() => {
        this.loading = false;
        this.is_task_positions_dirty = false;
      });
    },
    updateIsComplete(emitted, id) {
      this.task_permit_types.find((t) => t.id == id).is_complete = emitted;
    },
    updateFormData(emitted, id) {
      this.task_permit_types.find((t) => t.id == id).form_data =
        emitted.form_data;
      this.task_permit_types.find((t) => t.id == id).start_date_time =
        emitted.start_date_time;
      this.task_permit_types.find((t) => t.id == id).end_date_time =
        emitted.end_date_time;
    },
    closeModal(modal_name) {
      this.loading = false;
      this.$bvModal.hide(modal_name);
    },
    sendFormData(vbModalEvt, id) {
      if (vbModalEvt) {
        vbModalEvt.preventDefault();
      }
      if (this.user.role_id == 3) {
        Confirmation.swalCustomConfirm(
          "A fent megnevezett munkafolyamatot engedélyezem!",
          "Igen",
          "Az adott helyszínen történő biztonságos munkavégzéshez szükséges speciális, lokális ismeretek, utasítások az elrendelővel ismertetve lettek. A berendezésbe történő anyagbejutás megakadályozása megtörtént."
        ).then((response) => {
          if (response.isConfirmed === true) {
            this.loading = true;
            this.$refs["permit_form_" + id][0].sendFormData();
          }
        });
      } else {
        this.loading = true;
        this.$refs["permit_form_" + id][0].sendFormData();
      }
    },
    unplug_user_changed(position) {
      this.task_positions
        .filter(
          (tp) =>
            tp.unplug == position.unplug &&
            tp.position_simple.is_high_voltage ==
              position.position_simple.is_high_voltage
        )
        .forEach((p) => {
          if (
            p.position_simple.is_high_voltage == 1 ||
            p.unplug_user_id == null
          ) {
            this.setUnplugUserId(p.id, position.unplug_user_id);
          }
        });
    },
    lock_id_changed(position) {
      this.task_positions
        .filter(
          (tp) =>
            (tp.lock_id === true ||
              tp.lock_id === false ||
              tp.lock_id == null) &&
            tp.locking == position.locking &&
            tp.position_simple.is_high_voltage ==
              position.position_simple.is_high_voltage
        )
        .forEach((p) => {
          this.setLockId(p.id, position.lock_id);
        });
    },
    setUnplugUserId(id, unplug_u_id) {
      this.task_positions[
        this.task_positions.map((tp) => tp.id).indexOf(id)
      ].unplug_user_id = unplug_u_id;
    },
    SetLockIdToNull(id) {
      this.$delete(
        this.free_locks.find(
          (f) =>
            f.position_id ==
            this.task_positions.find((tp) => tp.id == id).position_id
        ),
        "position_id"
      );
      this.task_positions[
        this.task_positions.map((tp) => tp.id).indexOf(id)
      ].lock_id = null;
    },
    setLockId(id, l_id) {
      this.$set(
        this.task_positions[this.task_positions.map((tp) => tp.id).indexOf(id)],
        "lock_id",
        l_id
      );
      this.$forceUpdate(
        this.task_positions[this.task_positions.map((tp) => tp.id).indexOf(id)]
      );
    },
    deletePermit(task_permit) {
      Confirmation.swalDelete().then((response) => {
        if (response.isConfirmed === true) {
          Task.deletePermit(task_permit.id).then(() => {
            this.task_permit_types[
              this.task_permit_types
                .map((tpt) => tpt.id)
                .indexOf(task_permit.task_permit_type_id)
            ].permits.splice(
              this.task_permit_types[
                this.task_permit_types
                  .map((tpt) => tpt.id)
                  .indexOf(task_permit.task_permit_type_id)
              ].permits
                .map((p) => p.id)
                .indexOf(task_permit.id),
              1
            );
          });
        }
      });
    },
    addPermitToTask(bvModalEvt) {
      if (bvModalEvt) {
        bvModalEvt.preventDefault();
      }
      this.loading = true;

      Task.addPermit(this.permit_to_add)
        .then(() => {
          //24.07.25. Azért kommenteltem ki,mert ha lassú a net, előbb frisíti le a szerverrlől és a lefrissített válaszhoz még egyszer hozzáadja. volt a callback functionnek response parmétere
          //this.task_permit_types[this.task_permit_types.map((tpt) => tpt.id).indexOf(response.data.task_permit_type_id)].permits.push(response.data);
          this.$bvModal.hide("add_permit_" + this.permit_to_add.permit_type);
          this.permitToAddInitializer(
            this.task_permit_types.find(
              (p) => p.id == this.permit_to_add.permit_type_id
            )
          );
          this.errors = [];
        })
        .catch((error) => {
          this.loading = false;
          if (error.response.data.errors) {
            this.errors = error.response.data.errors;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    lockSelected(obj, color) {
      let vue = this;
      new Promise((resolve) => {
        let user_id = null;
        switch (color) {
          case 1:
            user_id = vue.task.ddc_user_id;
            break;
          case 4:
            user_id = vue.task.partner_user_id;
            break;
        }
        resolve({ user_id: user_id });
      }).then((response) => {
        Task.setPositionLock(this.task.id, {
          task_position_id: obj.position_id,
          user_id: response.user_id,
          lock_id: obj.lock_id,
        }).then(() => {
          this.task_positions
            .find((tp) => tp.id == obj.position_id)
            .locks.push({ color: color, id: obj.lock_id });
        });
      });
    },
    savePositions() {
      if (
        this.task_positions.filter(
          (tp) =>
            ((tp.lock_id == null || tp.lock_id == false) && tp.locking == 1) ||
            (tp.unplug_user_id == null && tp.unplug == 1)
        ).length != 0
      ) {
        this.$swal({
          toast: true,
          position: "bottom-right",
          iconColor: "red",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          icon: "error",
          title:
            "Válasszon lakatot és villanyszerelőt minden szükséges helyen!",
        });
      } else if (
        [...new Set(this.task_positions.map((t) => t.lock_id))].filter(
          (a) => a != null && a != true
        ).length !==
        this.task_positions
          .map((t) => t.lock_id)
          .filter((a) => a != null && a != true).length
      ) {
        this.$swal({
          toast: true,
          position: "bottom-right",
          iconColor: "red",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          icon: "error",
          title:
            "Ellenőrizze a lakatokat! Egy lakatot csak egy hajtáshoz rendelhet",
        });
      } else {
        Task.setElectriciansAndLocks(this.task.id, {
          task_positions: this.task_positions.map((p) => ({
            position_id: p.position_id,
            electrician_id: p.unplug_user_id,
            lock_id: p.lock_id,
          })),
        }).then((response) => {
          this.task.started_at = response.data.started_at;
        });
      }
    },
    addPosition() {
      this.$set(this, "is_task_positions_dirty", true);
      Task.addPositionToTask(this.task.id, this.position_to_add.id).then(() => {
        this.position_to_add = null;
        this.showTaskPositions();
      });
    },
    addPermitType() {
      Task.addPermitTypeToTask(this.task.id, this.permit_type_to_add.id).then(
        () => {
          this.permit_type_to_add = {};
          this.showPermitTypes();
        }
      );
    },
    hasNecessaryUsers(task_permit_type) {
      /*
			1 - tűzgyújtási -> munkavégző
			2 - beszállási -> figyelő, irányító
			3 - beszállási külső -> irányító
			4 - magasaban végzett -> munkavégző
			*/

      if ([1, 4].includes(task_permit_type.permit_type)) {
        // tűz vagy magas
        return task_permit_type.permits.length;
      } else if (task_permit_type.permit_type == 2) {
        // belső
        let hasLeader =
          task_permit_type.permits.filter((p) => p.local_role == 3).length >= 1;
        let hasObserver =
          task_permit_type.permits.filter((p) => p.local_role == 2).length >= 1;
        return hasObserver && hasLeader;
      } else if (task_permit_type.permit_type == 3) {
        //tűz vagy külső
        let hasLeader =
          task_permit_type.permits.filter((p) => p.local_role == 3).length == 1;
        console.log(hasLeader);
        return hasLeader;
      }
      return false;
    },
    users_for_permits(task_permit_type) {
      if (!this.users) {
        return [];
      }
      let users = this.users;

      if (task_permit_type.permit_type == 1) {
        users = users.filter((u) => u.has_valid_fire_certificate == true);
      }
      if (task_permit_type.permit_type == 3) {
        users = users
          .filter((u) => u.partner_id == this.task.partner_id)
          .filter((u) => u.role_id == 6);
      }
      if (this.hasRadiatingUnits) {
        users = users.filter(
          (u) => u.has_valid_radiation_protection_certificate == true
        );
      }

      return users.map((u) => ({
        id: u.id,
        name:
          u.last_name +
          " " +
          u.first_name +
          (u.name_extension != null ? " " + u.name_extension : "") +
          (u.partner_id != null ? " (" + u.partner_name + ")" : ""),
        pin_set: u.pin_set,
      }));
    },
    blackLockReadyForReturn() {
      let locationPermit = false;
      if (
        this.task_permit_types &&
        this.task_permit_types.filter((t) => t.permit_type == 5).length > 0
      ) {
        const locationPermits = this.task_permit_types.filter(
          (t) => t.permit_type == 5
        );
        locationPermit = locationPermits[0];
      }

      //akkor lehet visszahozni a fekete lakatot, ha a belépett user ennek a mf-nak az operátora és a
      //mf státusza folyamatban vagy lezárható, illetve a mt átadás-átvételit visszahozták
      return (
        this.user.id == this.task.operator_id &&
        this.user.role_id == 7 &&
        (this.task.status < 50 ||
          this.task.status == 60 ||
          (locationPermit && locationPermit.status == 90))
      );
    },
    closeAreaPermitModal() {
      this.$bvModal.hide("additionalAreaPermitModal");
    },
    initializeAreaPermitForm() {
      Task.hasBlackLocksReturned(this.task.id).then((response) => {
        this.hasBlackLocksReturned = response.data.hasBlackLocksReturned;
      });
    },
    storeAreaPermit() {
      Task.storeAreaPermit(this.task.id, this.areaPermitForm).then(() => {
        this.areaPermitForm = {};
        this.showPermitTypes();
      });
    },
  },
};
</script>

<style></style>
